import { ACTIVE_FLOOR_FEATURES, OTHER_FLOOR_FEATURES, FloorRenderFeatures } from "./floor_render_features"
import { hatchAt, HATCH_BREAK_MS } from "game-common/room/hatch"
import { HatchSprite, HatchBreakSprite, attachHatchSprite, attachHatchBreakSprite } from "./hatch_sprite"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { Mechanics } from "game-common/mechanics/mechanics"
import { contentTexture } from "./content_textures"
import { CompositeTilemap } from "@pixi/tilemap"
import { NormalBlendTilemap } from "./normal_blend_tilemap"
import { MapDelta, TileEffect } from "game-common/map/map"
import { Coordinate, Entity, EntityId, TileMap, TileSize, Visibility, WindLeavesConfig, getCurrentTime } from "game-common/models"
import { ResettableIterator, roughToFineCoordinates } from "game-common/util"
import { BLEND_MODES, Container, DisplayObject, Graphics, Sprite, Texture, IPointData, Rectangle, Renderer, filters } from "pixi.js"
import { logger } from "../../../logger"
import { HoleEffect } from "./effects/hole_effect"
import { FloorOpeningEdges } from "./floor_opening_edges"
import { stackedLadderState, StackedLadderSprite } from "./stacked_ladder"
import { ladderVeils } from "./effects/ladder_extension"
import { StoreyEdgeLayer } from "./storey_edges"
import { publishStoreyVisibility } from "./storey_visibility"
import {
    ITileEffectSystem,
    SurfaceObject,
    TileEffectSystem,
    TileEffectSystemConfig,
} from "./effects/tile_effect_system"

import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { ClientGameLogic } from "../../../client_game_logic"
import { IMapRenderingManager, RenderResult } from "../../../client_models"
import { CameraManagementService } from "./camera_management_service"
import { CompositeTileRenderer } from "./composite_tile_renderer"
import { MinimapSurface } from "./minimap_surface"
import { ScreenShakeManager } from "./screen_shake_manager"
import { ISkyManager, SkyManager, SkyManagerConfig } from "./sky_manager"
import { SpriteManagementService } from "./sprite_management_service"
import { spriteContentWorldBounds } from "./sprites/texture_content_bounds"
import { TileTextureExtractor } from "./sprites/tile_texture_extractor"
import { WorldOverlaySurface } from "./world_overlay_surface"
import { GrassSway } from "./effects/grass_sway"
import { spritedTileAnchorY, spritedTileZIndex } from "./sprited_tile_zindex"
import { EntityTileOccluderFade } from "./entity_tile_occluder_fade"
import { LavaTileFade } from "./effects/lava_tile_fade"
import { wallGrassFringeTexture } from "./effects/wall_grass_fringe"
import { bareEdges, EdgeProfile, edgeProfile, withBareEdges } from "./effects/wall_bare_edges"
import { SceneRoomRenderer } from "./scene_room_renderer"
import { roofGroundDepth } from "./building_depth"
import { RoofArtwork, RoofCompositeOverlap } from "./roof_composite_overlap"
import { withWallJointOverlap } from "./effects/wall_joint_overlap"
import { SoftenedGraphics } from "./softened_graphics"
import { BUILDING_SHADOW, BuildingShadowLayer, buildingShadowConfig, faceShadeTint, ROOF_SHADE, shadowLight } from "./building_shadow"
import { HALO, HaloPiece, SilhouetteHaloLayer, WALL_HALO_Z_INDEX } from "./silhouette_halo"
import { SHADE_FADE_MS } from "./baked_groups"
import { shadowBakeResolution } from "./device_tier"
import { pitchedRoofPiece } from "game-common/room/autotile_roof"
import { GroundRiseLayer } from "./ground_rise"
import { RockMassLayer } from "./rock_mass_layer"
import { WaterContact, WaterLayer } from "./water_layer"
import { getRendererApp } from "./renderer_app"
import { claimHeavyWork, heavyWorkTakesTurns } from "./frame_work_budget"
import { castBuildingShadows, castContactShadows, castRoofShadows, elevatedPostShadows, indoorShadeCells, polygonsKey, ShadowPolygon, ShadowStorey, shadowStackKey, sunReceivingRects } from "./building_shadow_geometry"
import { WORLD_SUN } from "./sun"
import { inBuildingShade, publishBuildingShade, setBuildingShadeAlpha, shadeTint, withdrawBuildingShade } from "./building_shade"
import { artShadePoints, furnitureShadePoints, multiplyTints, SHADED_ART_MAX_HEIGHT, shadeClimb, wallInShade } from "./sprite_shade"
import { ClimbShade } from "./climb_shade"
import { furniturePieces, furnitureShadows } from "./furniture_shadow"
import { compositeCasts, FOLIAGE_SHADOW, foliageShadowConfig, foliageShadows } from "./foliage_shadow"
import { ShadowStamp, stampsKey } from "./foliage_shadow_geometry"
import { RenderedTileOcclusion, SpriteBoundsFilter } from "./rendered_tile_occlusion"
import { VisibilityFade } from "./visibility_fade"
import { isExteriorFloorFall } from "game-common/floor_fall"
import type { EntityRenderingManager } from "./entity_rendering_manager"
import { cellKey, hasHouseRoof, houseRoofOffset, insideHouse, roofCellsForHouse, STOREY_RISE_PX, ROOF_UPLIFT_PX, TwoStoreyHouse } from "game-common/room/ember/two_storey_house"
import { isRelatedFloorTransfer } from "../../../related_room_transfer"
import { roofVisibleBounds } from "game-common/room/ember/roof_support"
import { stairExitLanding } from "game-common/room/ember/stairs"
import { applyStairOrientation } from "./stair_orientation"
import { buildHouseRoof } from "game-common/room/ember/house_roof"
import { coversWallBelow, ROOF_AUTOTILE_DEFINITIONS, WALL_AUTOTILE_DEFINITIONS, WallAutotileDefinition, wallSeamPiece } from "game-common/room/autotile_library"
import type { RoofAutotileDefinition } from "game-common/room/autotile_roof"
import { tileSetSpecs } from "game-common/room/base_map_builder"
import { ItemTypeCharacteristics } from "game-common/item/item_type_characteristics"
import { WallTrimAtlas, anyWallTrims, archOpening, bakeWallCell, storeyFloored, storeyStack, wallTrimPainter } from "./effects/wall_trim_bake"
import {
    buildLandings, endLandingGhosts, Landing, LandingDust, landingDurationMs, landingDustColors, landingImpactMs, LandingLayer,
    landingPose, poseSprites, restSprites,
} from "./effects/build_landing"
const seedrandom = require("seedrandom")

/** The widest doorway a lintel spans: the most cells the storey above may wall over (the server's GATEWAY_SPAN). */
const LINTEL_SPAN = 3

/** A sprite shaded where it stands (shadeSprites): how far into shade it should be, and is drawn now. */
/** A sprite in shade (shadeSprites). `line`: on tall art, the art's row where the shade it climbs ends (ClimbShade). */
interface ShadedSprite { key: string; sprite: Sprite; face: boolean; target: number; shown: number; line?: number }

/** A wall piece (see renderSpritedCell): `wallFace` shows its south face, shaded with the sun's shadows. */
type WallFaceSprite = Sprite & { wallFace?: boolean; wallArt?: boolean }

interface CaptureGroup {
    x: number
    y: number
    tile: string
    context?: any
    delta?: MapDelta
}

/** What a map is FOR, as opposed to what it has drawn: the active room's, or a passive floor's. */
interface MapRole {
    surface: Container
    skySurface: Container
    worldOverlaySurface: WorldOverlaySurface
    miniMapSurface: MinimapSurface
    sceneRenderer: SceneRoomRenderer
    cameraManagementService: CameraManagementService
    screenShakeManager: ScreenShakeManager
    grassSway?: GrassSway
    features: Readonly<FloorRenderFeatures>
    projectionFloor?: number
    onHandOff?: (next: MapRenderingManager) => void
}

/**
 * Move many children into one container. Container.removeChild finds each child with indexOf and
 * splices it out, so moving a floor's few thousand sprites one at a time is quadratic: a hitch of
 * its own. Each old parent is filtered once instead.
 */
const moveChildren = (children: DisplayObject[], to: Container): void => {
    const byParent = new Map<Container, Set<DisplayObject>>()
    for (const child of children) {
        if (!child.parent || child.parent === to) continue
        let moving = byParent.get(child.parent)
        if (!moving) byParent.set(child.parent, moving = new Set())
        moving.add(child)
    }
    byParent.forEach((moving, parent) => {
        const kept = parent.children.filter(child => !moving.has(child))
        parent.children.length = 0
        for (const child of kept) parent.children.push(child)
        moving.forEach(child => { (child as any).parent = null; to.addChild(child) })
        // What removeChild would have done for the parent: its bounds and child-change hook.
        ;(parent as any)._boundsID++
        ;(parent as any).onChildrenChange(0)
    })
}

/** A stack's wall-trim signature (MapRenderingManager.wallTrimSignature), by its bottom storey, shared by its storeys' maps. */
const stackTrimSignatures = new WeakMap<TileMapMetaProvider, { version: string; definitions: readonly unknown[]; signature: string }>()

export class MapRenderingManager implements IMapRenderingManager {
    // Bends the room's grass in the wind (GrassSway). Optional: a renderer that does not want the
    // effect, or a test, simply never sets one, and every call site is guarded. The blades themselves
    // come from the COMPOSITE tile path, not from the sprited-layer cells below -- long grass is a
    // two-tile composite -- so this is handed to the CompositeTileRenderer rather than used here.
    private grassSway: GrassSway | undefined
    /**
     * Set on the active room's map by its owner, which is told when a change of level hands the active
     * role to the map that already drew the destination floor (see handOverTo). Moves with the role.
     */
    onHandOff?: (next: MapRenderingManager) => void
    // Build ghosts placed with addTileAt(owned): this map's art, so they move with it.
    private ownedGhosts = new Set<Sprite>()
    // Pieces landing as they are built (effects/build_landing.ts): their dust, the copies this map draws
    // of a peer's flat pieces (one cell of a tilemap can't be moved), and the landings already put to rest.
    private landingDust = new LandingDust(() => this.surface)
    private landingOverlays = new Map<Landing, { sprites: Sprite[]; pivot: { x: number; y: number } }>()
    private settledLandings = new WeakSet<Landing>()
    // The room this map last painted in full. Deltas that arrive before it (a room's own state while it
    // loads) are what is already there, not something being built.
    private paintedRoomId?: string

    private surface: Container
    private worldOverlaySurface: WorldOverlaySurface
    private skySurface: Container
    private tilemapRenderers: ResettableIterator<CompositeTilemap>
    private tilemapRendererList: CompositeTilemap[]
    private lavaTileFade = new LavaTileFade()
    private lavaFadeSurfaces = new Map<CompositeTilemap, Container>()
    private tilemapMetaProvider: TileMapMetaProvider
    private visibility?: Visibility
    private needsRender: boolean = false
    private miniMapSurface: MinimapSurface
    private screenShakeManager: ScreenShakeManager
    private compositeTileRenderer: CompositeTileRenderer
    private compositeTileSpriteTextures: Map<string, Texture> = new Map()
    // Sprited-layer sprites owned per-cell (key `${x}-${y}-${layerId}`), NOT drawn from the frame
    // sprite pool -- so a single cell can be repainted incrementally on a map delta / redrawClient
    // without the LIFO pool's all-or-nothing release. Destroyed on full render + incremental removal.
    private spritedCellSprites: Map<string, Sprite[]> = new Map()
    // Which layer ids have actually rendered sprited cells, so a per-cell lookup can check just those
    // keys instead of scanning the whole map's worth of them. Populated as cells render.
    private spritedLayerIds: Set<string> = new Set()
    // Fades map tile sprites (tree/bush canopies, cave ore blocks) the local player stands behind
    // toward translucent, so the player is never fully hidden by them. Alpha-only; see applyOccluderFade.
    private occluderFade: EntityTileOccluderFade = new EntityTileOccluderFade()
    // The surfaceId (if any) each sprited cell registered its owned sprites under, so an incremental
    // remove can detach those exact sprites from the shared surfaceObjectsByKey spriteSet (which the
    // tile-effect system reads). Without this, removeSpritedCell destroys the sprites but leaves dead
    // references in the spriteSet, and a later hit reads `.x`/`.texture` on a destroyed sprite -> throw.
    private spritedCellSurfaceId: Map<string, string> = new Map()
    // Cells (key `${x}-${y}-${layerId}`) changed by applied map deltas since the last repaint. On a
    // redrawClient signal we repaint just these incrementally (sprited layers) instead of the whole
    // map; if any dirty cell is a tilemap/other layer we fall back to a full rerender. Cleared on
    // both paths.
    private dirtyCells: Set<string> = new Set()
    private pendingRedraw: boolean = false
    // Set when a dirty batch includes a removal or a damaged (hp < hpMax) tile. Those cells must go
    // through the FULL rerender path: the tile-effect system (crumble, wall shake, damage holes) owns
    // and reads these sprites, and an incremental remove+recreate races/breaks it. Pristine placements
    // (new tile, hp == hpMax / no hp) stay incremental.
    private dirtyNeedsFull: boolean = false
    private skyManager: ISkyManager
    private tileEffectSystem: ITileEffectSystem
    private surfaceObjectsByKey: Map<string, SurfaceObject> = new Map()
    private cameraManagementService: CameraManagementService
    private spriteOcclusionBounds = new Rectangle()
    private spriteBoundsFilter = new SpriteBoundsFilter()
    private renderedOcclusionTiles = new RenderedTileOcclusion()
    private sceneRenderer: SceneRoomRenderer
    private floorOpeningEdges = new FloorOpeningEdges()
    /** Where an upper floor ends without a wall under it: its lips, and the faces landing on this storey (storey_edges.ts). */
    private storeyEdges = new StoreyEdgeLayer()
    private houseRoof = new NormalBlendTilemap()
    private roofGroups = new Map<string, { surface: Container; tiles: CompositeTilemap }>()
    // Tiles a build preview shows reshaped, `${x}-${y}-${layerId}`: hidden while their ghosts stand in.
    private buildVeil = new Set<string>()
    /** This storey's wall cells that arch over a gateway of the storey below: their floor is left undrawn ("x,y"). */
    private archedCells = new Set<string>()
    // The tilemap each ground layer was drawn into on the last full render, so one layer can be re-laid alone.
    private groundLayerRenderers = new Map<string, CompositeTilemap>()
    // The sky layers in the order a full render lays them into the roof groups.
    private skyTilemaps: TileMap[] = []
    private roofGroupByCell = new Map<string, string>()
    private roofArtwork = new Map<Container, Rectangle[]>()
    private roofCompositeOverlap: RoofCompositeOverlap
    private sceneCompositeOverlap: RoofCompositeOverlap
    private sceneCompositeKey?: string
    private compositeSprites: Sprite[] = []
    private artworkVersion = 0
    /** Sprites darkened by the shade they stand in, and wall faces (shadeSprites): which, and the tints last applied. */
    private shadedSprites: ShadedSprite[] = []
    /** How far into shade each sprite was last drawn, by where it stands (shadeSprites). */
    private shadeShown = new Map<string, number>()
    private shadedSpritesSeen = false
    private shadeEasing = false
    private shadeEasedAt?: number
    private shadedSpritesKey?: string
    private shadedTints?: string
    /** Tall art the buildings' shade climbs, and its shade, by the art's ShadedSprite key. */
    private climbing: ShadedSprite[] = []
    private climbShades = new Map<string, ClimbShade>()
    getCompositeSprites = (): Sprite[] => this.compositeSprites
    getArtworkVersion = (): number => this.artworkVersion
    // Soften only the depth cues; the pixel artwork remains crisp.
    private houseRoofDepth = new SoftenedGraphics(1.25, 2)
    /** The roof lit by its slope: its front darkening down to a shaded eave (renderHouseRoof), multiplied over it. */
    private houseRoofShade = new Graphics()
    /** The roof's lift through its storey's tone (setRoofTone), and the gain it was last set to. */
    private roofTone?: InstanceType<typeof filters.ColorMatrixFilter>
    private roofToneKey?: string
    /** The same spill round this storey's walls, onto what lies behind them (a merlon on the roof below). */
    private wallHalo = new SilhouetteHaloLayer()
    private renderedHaloKey?: string
    private houseRoofCells = new Set<string>()
    /**
     * The buildings' own shadows as last cast, and what they were cast from (the storeys' walls, floors and roofs, the light):
     * kept through the tile changes that leave every storey's structure as it was. Plants and contact shade are re-cast.
     */
    private castBuildings?: { key: string; polygons: ShadowPolygon[]; apart: ShadowPolygon[]; posts: ShadowPolygon[]; roofKey?: string; roof?: ShadowPolygon[] }
    private viewEntity?: Entity
    setViewEntity = (entity: Entity) => { this.viewEntity = entity }
    private houseRoofFade = new VisibilityFade()
    private projectionFloor?: number
    private renderedStoreyRisePx = STOREY_RISE_PX
    private renderedRoofUpliftPx = ROOF_UPLIFT_PX
    /** The storey above as last checked, and which of this storey's roof cells its floor covered. */
    private checkedUpperFloorVersion?: string
    private renderedRoofCover = ""
    /** What the storeys above and below last looked like to this storey's walls (wallSeamCells), and when it was checked. */
    private checkedWallSeamVersion?: string
    private renderedWallSeams = ""
    private checkedWallTrimVersion?: string
    /** Wall trims (game-common room/wall_trims.ts): the atlas this storey's trimmed cells are baked into, and the walls they were drawn for. */
    private wallTrimAtlas = new WallTrimAtlas()
    private renderedWallTrims = ""
    private houseRoofFallback = new Map<string, string>()
    /** The sun's shadows of this storey's buildings and the storeys stacked above it (see updateBuildingShadow). */
    private buildingShadow = new BuildingShadowLayer()
    /** Soft shade where floors meet anything standing on them (castContactShadows), drawn with the sun's. */
    private contactShadow = new BuildingShadowLayer()
    /**
     * The sun's shadows the storeys above throw over this storey's house roof (castRoofShadows), drawn over its art and
     * gone with it. The storey above takes the shade on the roof as its own (bodies walking there) but leaves the drawing to
     * this one: blurred above the player it is drawn apart, and its shade would darken nothing down here.
     */
    private roofCastShadow = new BuildingShadowLayer()
    private renderedRoofShadowGeometry?: string
    private roofShadowPolygons: ShadowPolygon[] = []
    /** The step along the room's raised ground, between map1 and map2 (see ground_rise.ts). */
    private groundRise = new GroundRiseLayer()
    /** An organic cave's rock, drawn as one mass (rock_mass_layer.ts); empty in every other room. */
    private rockMass = new RockMassLayer()
    /** The room's water (see water_layer.ts): directly above the raised-ground step, below map2. */
    private water = new WaterLayer()
    private checkedShadowVersion?: string
    private renderedShadowGeometry?: string
    /** Which way the shadows were last cast: the sun's by day, the moon's by night (building_shadow.ts shadowLight). */
    private shadowSun: { x: number; y: number } = { ...WORLD_SUN }
    private shadowPolygons: ShadowPolygon[] = []
    /** The plants' silhouettes drawn with them (foliage_shadow_geometry.ts). */
    private shadowStamps: ShadowStamp[] = []
    /** Long grass's lighter silhouettes, drawn with them but not published as shade. */
    private grassStamps: ShadowStamp[] = []
    private renderedContactGeometry?: string
    private contactPolygons: ShadowPolygon[] = []
    /** Cells under this storey's own cover (the storey above's floor, its roofs) while interiors keep out of the sun's shade. */
    private wallCover?: ReadonlySet<string>
    /** A room seen through an arch cut over its doorway in this storey, darkened (BUILDING_SHADOW.arch). */
    private archShade = new Graphics()
    /** When to look again for trees and bushes whose art had not loaded yet (foliageShadows' `pending`). */
    private foliageRetryAt?: number
    /** The room whose shade this map last published for the bodies in it (building_shade.ts). */
    private shadeRoomId?: string
    /** Whether the last full render left out the old roof drop shadow because building shadows were on. */
    private renderedWithBuildingShadow?: boolean
    private pitchedRoofTiles?: { source: readonly RoofAutotileDefinition[]; tiles: Set<string> }

    private roomId: EntityId
    private RANDOM_NUMBER_GENERATOR
    private spriteManagementService: SpriteManagementService
    private rngCache: Map<string, number> = new Map()
    private logic: ClientGameLogic

    // Rendering state lock and queue system for race condition prevention
    private isRendering: boolean = false
    private pendingMapDeltas: MapDelta[] = []
    private pendingRerender: boolean = false

    constructor(
        surface: Container,
        skySurface: Container,
        miniMapSurface: MinimapSurface,
        worldOverlaySurface: WorldOverlaySurface,
        logic: ClientGameLogic,
        private features: Readonly<FloorRenderFeatures> = ACTIVE_FLOOR_FEATURES,
    ) {
        this.surface = surface
        this.roofCompositeOverlap = new RoofCompositeOverlap(surface)
        this.sceneCompositeOverlap = new RoofCompositeOverlap(surface)
        this.surface.addChild(this.houseRoofDepth, this.houseRoof, this.houseRoofShade, this.floorOpeningEdges,
            this.buildingShadow, this.contactShadow, this.roofCastShadow, this.wallHalo, this.archShade, this.rockMass)
        this.houseRoofShade.blendMode = BLEND_MODES.MULTIPLY
        this.wallHalo.zIndex = WALL_HALO_Z_INDEX
        // Over the floor and its shades, under every wall: the wall's art hides it all but in the arch's opening.
        this.archShade.zIndex = WALL_HALO_Z_INDEX + 0.1
        this.archShade.interactive = false
        this.houseRoofDepth.interactive = false
        this.sceneRenderer = new SceneRoomRenderer(surface, (root, sky) => {
            const map = new MapRenderingManager(root, sky, miniMapSurface,
                new WorldOverlaySurface(logic, root, OTHER_FLOOR_FEATURES), logic, OTHER_FLOOR_FEATURES)
            // Companion rooms are seen from another floor. The active player's coordinates
            // must never open a cutaway in the downstairs roof when viewed from upstairs.
            map.init()
            return map
        }, (entity, provider) => (logic.clientRenderer.entity() as EntityRenderingManager).renderSceneEntity(entity, provider),
            (entity, sprite) => (logic.clientRenderer.entity() as EntityRenderingManager).retainLevelEntity(entity, sprite),
            id => logic.getEmberRole(id))
        this.skySurface = skySurface
        this.miniMapSurface = miniMapSurface
        this.worldOverlaySurface = worldOverlaySurface
        this.screenShakeManager = new ScreenShakeManager()
        this.logic = logic

        // Initialize camera management service
        this.cameraManagementService = new CameraManagementService({
            surface: this.surface,
            worldOverlaySurface: this.worldOverlaySurface,
            screenShakeManager: this.screenShakeManager,
            aimAngle: entity => entity === logic.playerEntity ? logic.playerController?.rangedAimAngle() : undefined,
        })

        // Initialize sprite management service
        this.spriteManagementService = new SpriteManagementService({
            spritePoolSize: 2000,
            defaultSurface: this.surface,
        })
        // Initialize sky manager
        const skyConfig: SkyManagerConfig = {
            skySurface: this.skySurface,
            tilemapMetaProvider: null as any, // Will be updated in update method
        }
        this.skyManager = new SkyManager(skyConfig)

        // Initialize composite tile renderer first
        this.compositeTileRenderer = new CompositeTileRenderer({
            surface: this.surface,
            skySpriteSurfaceAbove: null as any, // Will be set after sky manager is created
            tilemapMetaProvider: null as any, // Will be updated in update method
            compositeTileSpriteTextures: this.compositeTileSpriteTextures,
            surfaceObjectsByKey: this.surfaceObjectsByKey,
            attachTexture: this.spriteManagementService.attachTexture,
            getGrassSway: () => this.features.grassSway ? this.grassSway : undefined,
            depthSortedRoofs: true,
            pertubation: this.pertubation,
            pctChance: this.pctChance,
            logic: this.logic,
            tileTextureExtractor: TileTextureExtractor.instance,
        })

        // Initialize tile effect system
        const tileEffectConfig: TileEffectSystemConfig = {
            surface: this.surface,
            worldOverlaySurface: this.worldOverlaySurface,
            tilemapMetaProvider: null as any, // Will be updated in update method
            holeEffect: new HoleEffect("", () => Math.random()),
            skySpriteSurfaceAbove: null as any, // Will be set after sky manager is created
            skySpriteSurfaceBelow: null as any, // Will be set after sky manager is created
            attachTexture: this.spriteManagementService.attachTexture,
            renderCompositeTile: this.renderCompositeTile,
            handleRenderCompositeTile: this.compositeTileRenderer.handleRenderCompositeTile,
        }
        this.tileEffectSystem = new TileEffectSystem(tileEffectConfig, this.surfaceObjectsByKey)
    }

    // ============================================================================
    // INITIALIZATION
    // ============================================================================

    init = () => {
        this.tilemapRendererList = [
            new NormalBlendTilemap(),
            new NormalBlendTilemap(),
            new NormalBlendTilemap(),
            new NormalBlendTilemap(),
            new NormalBlendTilemap(),
            new NormalBlendTilemap(),
        ]
        this.tilemapRenderers = new ResettableIterator<CompositeTilemap>(this.tilemapRendererList)
        this.tilemapRendererList.forEach((renderer, i) => {
            this.surface.addChild(renderer)
            // Same ground sort order, immediately above this layer and below the next one.
            const fades = new Container()
            this.surface.addChild(fades)
            this.lavaFadeSurfaces.set(renderer, fades)
        })

        // Update composite tile renderer with sky surfaces from SkyManager
        this.compositeTileRenderer.updateSkySurfaces(this.skyManager.getSkySpriteSurfaceAbove())

        // Update tile effect system with sky surfaces from SkyManager
        this.tileEffectSystem.updateSkySurfaces(
            this.skyManager.getSkySpriteSurfaceAbove(),
            this.skyManager.getSkySpriteSurfaceBelow(),
        )

        // Live tuning hook: window.occluderFade.enabled / .alpha / .speed / .minNeighbors etc.
        // (matches window.deathEffect).
        if (this.features.occluderFade && typeof window !== "undefined") {
            ;(window as any).occluderFade = this.occluderFade.config
        }
    }

    // ============================================================================
    // CORE RENDERING METHODS
    // ============================================================================

    /**
     * Whether the storey's structure changed in a way only a full drawing shows: a floor upstairs covering this roof,
     * walls meeting anew (seams, lintels), trims reading the whole stack. Asked as the frame starts, and again once the
     * frame's deltas are in, so a wall laid this frame is drawn once, in full, not repainted cell by cell and then redrawn.
     */
    private checkStructure = (): void => {
        // A floor built or removed upstairs covers or uncovers this storey's roof: repaint it then.
        const upperFloorVersion = this.tilemapMetaProvider?.getUpperFloorVersion()
        if (upperFloorVersion !== this.checkedUpperFloorVersion) {
            this.checkedUpperFloorVersion = upperFloorVersion
            if (this.roofCover() !== this.renderedRoofCover) this.needsRender = true
        }
        // A wall built or removed on this storey or the ones above and below changes where walls meet
        // (seams, lintels): repaint then.
        const provider = this.tilemapMetaProvider
        const upperWallVersions: string[] = []

        const seenUpper = new Set<TileMapMetaProvider>()
        for (let upper = provider?.getUpperFloorProvider(); upper && !seenUpper.has(upper); upper = upper.getUpperFloorProvider()) {
            seenUpper.add(upper)
            upperWallVersions.push(`${upper.roomId}:${upper.getPresentationVersion()}`)
        }
        const wallSeamVersion = provider && `${provider.getPresentationVersion()}|${upperWallVersions.join(";")}|${this.wallJointCeiling()}|` +
            `${provider.getFloorSupportProvider()?.roomId}:${provider.getFloorSupportProvider()?.getPresentationVersion()}`
        if (provider && wallSeamVersion !== this.checkedWallSeamVersion) {
            this.checkedWallSeamVersion = wallSeamVersion
            if (this.wallSeamSignature() !== this.renderedWallSeams) this.needsRender = true
        }
        // Trims read the whole stack (ivy climbs every storey, an arch cuts the storey over a gateway): repaint when
        // any storey's walls change.
        // A lone storey repaints the cells a change touches and their neighbours, which is all its trims need.
        const trimStack = provider && anyWallTrims() ? storeyStack(provider).storeys : []
        const trimVersion = trimStack.length > 1 ? trimStack.map(p => `${p.roomId}:${p.getPresentationVersion()}`).join("|") : ""
        if (trimVersion !== this.checkedWallTrimVersion) {
            this.checkedWallTrimVersion = trimVersion
            if (this.wallTrimSignature() !== this.renderedWallTrims) this.needsRender = true
        }
    }

    renderTileMap = () => {
        // Art drawn afresh this frame (a felled tree's full render, a repainted cell) starts untinted, and
        // the shadows above were worked out before the frame's deltas: settle both again at the end.
        const artworkBefore = this.artworkSignature()
        if (this.renderedStoreyRisePx !== STOREY_RISE_PX || this.renderedRoofUpliftPx !== ROOF_UPLIFT_PX) {
            this.renderedStoreyRisePx = STOREY_RISE_PX
            this.renderedRoofUpliftPx = ROOF_UPLIFT_PX
            this.rerender()
        }
        if (this.features.roofCutaway) this.skyManager.updatePlayerLocation(this.viewEntity ?? this.logic.playerEntity)
        this.checkStructure()
        // The storey the player is on draws first: a block laid there shows before the storeys round it catch up.
        // Asked before the scene's other storeys ask for theirs (they draw in sceneRenderer.render, below).
        const renderTurn = this.features.camera && this.needsRender && this.paintedRoomId === this.roomId && claimHeavyWork()
        const provider = this.tilemapMetaProvider
        this.roofGroups.forEach(({ surface }) => {
            surface.visible = this.skySurface.visible
            surface.alpha = this.skyManager.getSkyAlpha()
        })
        // The storey the player stands on is drawn whole; the others publish their own fade as they draw.
        if (this.features.camera && provider) publishStoreyVisibility(provider.roomId, 1)
        this.sceneRenderer.render(this.tilemapMetaProvider?.getHousePresentation(), this.viewEntity ?? this.logic.playerEntity,
            this.features.camera && !!this.logic.isRoomRevealHolding?.())
        const house = this.tilemapMetaProvider?.getHousePresentation()
        // A live roof can be added to an open top floor without a room reload.
        this.houseRoof.y = hasHouseRoof(house) ? houseRoofOffset(house, this.projectionFloor ?? house.floor) : 0
        this.houseRoofDepth.y = this.houseRoofShade.y = this.houseRoof.y
        this.houseRoof.zIndex = this.houseRoofZIndex(house)
        this.houseRoofDepth.zIndex = this.houseRoof.zIndex - 0.5
        this.houseRoofShade.zIndex = this.houseRoof.zIndex + 0.01
        this.roofCastShadow.zIndex = this.houseRoof.zIndex + 0.02
        this.houseRoofFade.update(hasHouseRoof(house) && (!this.features.roofCutaway ||
            isExteriorFloorFall((this.viewEntity ?? this.logic.playerEntity)?.floorFall) ||
            (!!(this.viewEntity ?? this.logic.playerEntity)?.location && !insideHouse(house, (this.viewEntity ?? this.logic.playerEntity).location))))
        this.houseRoof.alpha = this.houseRoofDepth.alpha = this.houseRoofShade.alpha = this.houseRoofFade.alpha
        this.houseRoof.visible = this.houseRoofFade.visible
        this.houseRoofDepth.visible = this.houseRoofShade.visible = this.houseRoof.visible
        // Art restored at its full height until its height was read.
        if (!this.isRendering && this.roofCompositeOverlap.heightsRead()) this.rebuildRoofComposites()
        this.roofCompositeOverlap.sync()
        this.updateBuildingShadow()
        this.groundRise.tick()
        this.water.tick()
        if (this.lavaTileFade.update()) this.needsRender = true
        // Apply queued map deltas every frame, independent of the visual re-render gate
        // below. Deltas carry collision/blocking changes -- e.g. a chopped-down tree
        // becoming walkable. Previously deltas were only applied inside a full re-render,
        // but a tree cut does not trigger one (it sends a map delta, not a redrawClient),
        // so the client's collision map (tileTypesMap "blocking") stayed stale until the
        // next unrelated re-render or a reload. The player then predicts a collision the
        // server no longer enforces; reconciliation repeatedly yanks the player through the
        // now-empty space -- the shudder when walking where a tree used to be. Applying
        // deltas here mutates the persistent data model, so a later full re-render still
        // reflects them.
        this.processPendingDeltas()
        // After the deltas, so a cell mined this frame is drawn open this frame.
        this.rockMass.tick(this.tilemapMetaProvider, (this.viewEntity ?? this.logic.playerEntity)?.location)
        this.floorOpeningEdges.sync(this.tilemapMetaProvider)
        this.storeyEdges.sync(this.tilemapMetaProvider, this.surface, claimHeavyWork)
        this.syncDownstairsVisibility()
        this.updateHatches()
        for (const ladder of this.stackedLadders) {
            if (ladder.destroyed || !ladder.parent) this.stackedLadders.delete(ladder)
            else ladder.pose()
        }
        this.applyLadderVeils()

        // The deltas may have changed what only a full drawing shows (a wall joining a trimmed stack): then that
        // drawing repaints their cells too, and a cell-by-cell repaint first would be thrown away.
        if (this.pendingRedraw) this.checkStructure()
        if (this.pendingRedraw && this.needsRender && !this.isRendering) {
            this.pendingRedraw = false
            this.dirtyCells.clear()
            this.dirtyNeedsFull = false
        }

        // A redrawClient signal: repaint just the delta-changed cells (incremental) rather than the
        // whole map. Runs here, after processPendingDeltas has applied the accompanying deltas. May
        // escalate to a full rerender (needsRender) if a tilemap-layer cell changed.
        if (this.pendingRedraw && !this.isRendering) {
            this.pendingRedraw = false
            this.repaintDirtyCells()
        }

        // A storey drawn again takes its turn with the other heavy work of the frame (frame_work_budget.ts), the trim
        // painter of a changed stack having had a turn of its own first; its first drawing in a room does not wait.
        if (!this.shouldRender() || (this.paintedRoomId === this.roomId && (!(renderTurn || claimHeavyWork()) || this.warmTrims()))) {
            this.updateLandings()
            this.syncSceneComposites()
            if (this.artworkSignature() !== artworkBefore) this.updateBuildingShadow()
            return
        }

        this.isRendering = true
        try {
            this.prepareRendering()
            this.renderAllLayers()
            this.groundRise.sync(this.tilemapMetaProvider)
            this.placeGroundRise()
            this.water.sync(this.tilemapMetaProvider)
            this.placeWater()
            this.finalizeRendering()
            // After the rebuild, which draws every wall at rest.
            this.updateLandings()
            this.syncSceneComposites()
        } finally {
            this.isRendering = false
        }

        if (this.artworkSignature() !== artworkBefore) this.updateBuildingShadow()

        // Process queued re-render request
        if (this.pendingRerender) {
            this.pendingRerender = false
            this.needsRender = true
        }
    }

    /**
     * Builds the trim painter of this storey's stack if a wall changed it (where its creepers grow is worked out over the
     * whole stack, ~20ms in a castle): true when it did, which was this frame's turn, so the drawing waits for the next.
     */
    private warmTrims = (): boolean => {
        const provider = this.tilemapMetaProvider
        if (!heavyWorkTakesTurns() || !provider || !anyWallTrims() || storeyStack(provider).storeys.length < 2) return false
        return wallTrimPainter(provider, this.wallSeamTileId).painter.warm()
    }

    /** Changes whenever this storey's sprites are drawn afresh or a composite comes or goes. */
    private artworkSignature = (): string => `${this.artworkVersion}|${this.surfaceObjectsByKey.size}`

    renderAt = (x: number, y: number, tilemap: TileMap, layerId: string, options?: any) => {
        if (layerId === "sprite2" && tilemap[x]?.[y] && tilemap[x][y] !== "0" && stackedLadderState(this.tilemapMetaProvider, { x, y })) {
            this.renderSpritedCell(x, y, tilemap[x][y], layerId, this.spritedLayerIndex(layerId))
            return this.spritedCellSprites.get(`${x}-${y}-${layerId}`)?.slice(-1)[0]
        }
        let { tilemapRender, wasReplaced, surfaceId } = options || {}

        tilemapRender = tilemapRender || this.tilemapRenderers.next().value
        const size = TileSize
        const _x = tilemap[x]
        if (!_x) {
            return
        }
        let spriteToReturn
        const tile = _x[y]
        if (tile && this.tilemapMetaProvider.shouldDrawTileAt(x, y, layerId, tilemap)) {
            if (!this.tilemapMetaProvider.isHiddenTile(tile)) {
                const compositeTileId = this.tilemapMetaProvider.getCompositeTileId(tile)
                if (compositeTileId) {
                    this.renderCompositeTile(this.tilemapMetaProvider, tile, compositeTileId, x, y)
                } else {
                    const tilesetTileId = this.tilemapMetaProvider.resolveTilesetTileId(tile)
                    if (layerId === "sky" && tilemap === this.tilemapMetaProvider.resolveTileMapFromLayerId("sky") &&
                        this.houseRoofCells.has(`${x},${y}`)) {
                        // The house roof is painted independently of the sky pass, beneath
                        // the upper room. Ordinary sky visibility must not control it.
                        return
                    }
                    if (layerId) {
                        const layerMapping = this.tilemapMetaProvider.resolveLayerMapping(layerId)
                        if (layerMapping.maptype === "sprited") {
                            spriteToReturn = this.spriteManagementService.createSpriteAt(tilesetTileId, x, y)
                        }
                        if (layerMapping.maptype === "tilemap") {
                            this.renderGroundTile(this.tilemapRenderers.values[layerMapping.idx], tilesetTileId, x, y, layerId)
                            if (wasReplaced) {
                                // todo -- this is too blunt... maybe there is a way to avoid full
                                // rerenders on pixijs tilemap renders
                                this.rerender()
                                return
                            }
                        }
                        if (layerMapping.maptype === "sky") {
                            const roof = this.roofGroupAt(x, y)
                            const delta = this.tilemapMetaProvider.getMapDeltaAt(x, y, "sky")
                            if (delta?.collideable) {
                                spriteToReturn = this.spriteManagementService.createSpriteAt(
                                    tilesetTileId,
                                    x,
                                    y,
                                    0,
                                    roof.surface,
                                )
                                spriteToReturn.y += houseRoofOffset()
                            } else if (!this.buildVeil.has(`${x}-${y}-sky`) ||
                                tilemap !== this.tilemapMetaProvider.resolveTileMapFromLayerId("sky")) {
                                roof.tiles.tile(`${tilesetTileId}`, x * size, y * size + houseRoofOffset())
                            }
                            const tileset = this.tilemapMetaProvider.resolveTileSet(tile)
                            const [left, top, right, bottom] = roofVisibleBounds(tileset?.tilesetId, Number(tile) - tileset?.firstGid)
                            this.recordRoofArtwork(roof.surface, new Rectangle(x * size + left,
                                y * size + houseRoofOffset() + top, right - left, bottom - top))
                            this.renderedOcclusionTiles.add(roof.surface,
                                new Rectangle(x * size, y * size + houseRoofOffset(), size, size))
                        }
                    } else {
                        this.renderGroundTile(tilemapRender, tilesetTileId, x, y, options?.fadeLayerId)

                        // Create overlay sprite for damage effects on regular layers
                        const delta = this.tilemapMetaProvider.findMapDeltaAt(x, y)
                        if (this.tileEffectSystem.shouldCreateDamageOverlay(delta)) {
                            spriteToReturn = this.tileEffectSystem.createDamageOverlaySprite(tilesetTileId, x, y)
                        }
                    }
                }
            }
        }

        if (spriteToReturn && layerId === "sprite2") {
            this.orientStairSprite(spriteToReturn, x, y, tile)
        }
        return spriteToReturn
    }

    private orientStairSprite = (sprite: Sprite, x: number, y: number, tile: string): void => {
        if (sprite instanceof StackedLadderSprite) { sprite.pose(); return }
        const type = this.tilemapMetaProvider.resolveTileMeta(tile).entityTileId
        if (type !== "stairs-up" && type !== "stairs-down") return
        ;(sprite as Sprite & { heightBlurExempt?: boolean }).heightBlurExempt = true
        applyStairOrientation(sprite, { x, y }, type, stairExitLanding(this.tilemapMetaProvider, { x, y }))
    }

    /** Descending stairs only have artwork where a floor or roof surrounds the opening. */
    private downstairsHasSurface = (x: number, y: number): boolean => {
        const provider = this.tilemapMetaProvider
        if (stackedLadderState(provider, { x, y })) return true
        const surfaces: [TileMapMetaProvider, string][] = [[provider, "map2"], [provider, "sky"]]
        const below = provider.getFloorSupportProvider()
        if (below) surfaces.push([below, "sky"])
        return surfaces.some(([map, layer]) => {
            const tiles = map.resolveTileMapFromLayerId(layer)
            for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) {
                const cx = x + dx, cy = y + dy
                const tile = tiles?.[cx]?.[cy]
                if (tile && tile !== "0" && !map.isHiddenTile(tile) &&
                    !map.getMapDeltaAt(cx, cy, layer)?.removed &&
                    (layer !== "map2" || !map.isFloorOpeningAt(cx, cy))) return true
            }
            return false
        })
    }

    private hatchSprites = new Set<HatchSprite | HatchBreakSprite>()
    private stackedLadders = new Set<StackedLadderSprite>()
    private ladderVeiled = new Set<Sprite>()

    /** A ladder extension draws this storey's stair art in its own place while it previews or grows. */
    private applyLadderVeils = (): void => {
        const storey = this.storey()
        const veiled = new Set<Sprite>()
        for (const veil of ladderVeils.active()) {
            if (storey < veil.from || storey > veil.to) continue
            this.spritedCellSprites.get(`${veil.x}-${veil.y}-sprite2`)?.forEach(sprite => { if (!sprite.destroyed) veiled.add(sprite) })
        }
        this.ladderVeiled.forEach(sprite => { if (!veiled.has(sprite) && !sprite.destroyed) sprite.alpha = 1 })
        veiled.forEach(sprite => { sprite.alpha = 0 })
        this.ladderVeiled = veiled
    }

    private updateHatches = (): void => {
        for (const hatch of this.hatchSprites) {
            if (hatch.destroyed || !hatch.parent || hatch.parent.destroyed) {
                if (!hatch.destroyed) hatch.destroy({ children: true })
                this.hatchSprites.delete(hatch)
            } else hatch.pose()
        }
    }

    private checkedDownstairsVisibility?: string
    private syncDownstairsVisibility = (): void => {
        const provider = this.tilemapMetaProvider
        if (!provider) return
        const below = provider.getFloorSupportProvider()
        const version = `${provider.roomId}:${provider.getPresentationVersion()}:${below?.roomId}:${below?.getPresentationVersion()}:${this.artworkVersion}`
        if (version === this.checkedDownstairsVisibility) return
        this.checkedDownstairsVisibility = version
        this.spritedCellSprites.forEach((sprites, key) => {
            const [x, y, layer] = key.split("-")
            const tile = provider.resolveTileMapFromLayerId(layer)?.[Number(x)]?.[Number(y)]
            if (!tile || provider.resolveTileMeta(tile).entityTileId !== "stairs-down") return
            const visible = !this.buildVeil.has(key) && this.downstairsHasSurface(Number(x), Number(y))
            sprites.forEach(sprite => { sprite.visible = visible })
        })
    }

    private renderGroundTile = (renderer: CompositeTilemap, texture: string, x: number, y: number, layerId?: string): void => {
        if (!renderer || !this.drawGroundTile(renderer, texture, x, y, layerId)) return
        if (layerId === "map2" && this.tilemapMetaProvider.twoStoreyHouse?.floor > 0) {
            this.recordRoofArtwork(renderer, new Rectangle(x * TileSize, y * TileSize, TileSize, TileSize))
        }
        // Recorded whatever this map's role: the active map becomes a passive floor without a repaint
        // when the player changes level (handOver), and passive floors test occlusion against these.
        const art = contentTexture(texture)
        this.renderedOcclusionTiles.add(renderer,
            new Rectangle(x * TileSize, y * TileSize, art.width, art.height))
    }

    /** Puts one ground tile into its layer, through its lava fade if one runs there. False when the fade drew it. */
    private drawGroundTile = (renderer: CompositeTilemap, texture: string, x: number, y: number, layerId?: string): boolean => {
        const key = `${x}-${y}-${layerId}`
        // A build preview is showing this tile reshaped; it stays recorded, just undrawn.
        if (layerId && this.buildVeil.has(key)) return true
        // The floor a wall stands on where it arches over a gateway below: the passage shows through the arch instead.
        if (layerId === "map2" && this.archedCells.has(`${x},${y}`)) return true
        const fadeSurface = this.lavaFadeSurfaces.get(renderer)
        if (fadeSurface && layerId && this.lavaTileFade.render(key, `${texture}`, x, y, fadeSurface)) return false
        renderer.tile(`${texture}`, x * TileSize, y * TileSize)
        return true
    }

    addTileAt = (x: number, y: number, tile: string, layerId: string, owned = false): RenderResult => {
        const mapping = this.tilemapMetaProvider.resolveLayerMapping(layerId)
        const tilesetTileId = this.tilemapMetaProvider.resolveTilesetTileId(tile)
        // Pending build ghosts must survive map redraws without being reused by the frame pool.
        const sprite = owned ? new Sprite(contentTexture(tilesetTileId)) :
            this.spriteManagementService.createSpriteAt(tilesetTileId, x, y, mapping?.idx || 0)
        if (owned) {
            sprite.position.set(x * TileSize, y * TileSize)
            sprite.zIndex = 100 + (mapping?.idx || 0) + y * TileSize + TileSize / 3
            this.surface.addChild(sprite)
            this.ownedGhosts.add(sprite)
        }
        if (mapping?.maptype === "sky") sprite.y += houseRoofOffset()
        if (layerId === "sprite2") {
            this.orientStairSprite(sprite, x, y, tile)
        }

        return {
            sprite,
        }
    }

    updateCompositeTilePointer = (at: IPointData) => {
        // An organic cave's rock has no sprites to hit-test: point at the rock cell under the pointer.
        if (this.rockMass.active) {
            const cell = this.rockMass.breakableCellAt(this.surface.toLocal(at))
            this.logic.playerController.updateCompositeTile(cell?.x ?? -1, cell?.y ?? -1)
            return
        }
        this.compositeTileRenderer.updateCompositeTilePointer(at)
    }

    renderCompositeTile = (
        tilemapMetaProvider: TileMapMetaProvider,
        parentTileId: string,
        compositeTileId: string,
        x: number,
        y: number,
        zIndex: number = 0,
    ) => {
        this.compositeTileRenderer.renderCompositeTile(tilemapMetaProvider, parentTileId, compositeTileId, x, y, zIndex)
    }

    // ============================================================================
    // PLAYER/CAMERA MANAGEMENT METHODS
    // ============================================================================

    updatePlayerLocation = (entity: Entity, force?: boolean) => {
        if (!entity) {
            return
        }

        // Update sky visibility based on player position
        if (this.features.roofCutaway) this.skyManager.updatePlayerLocation(entity, force)

        if (
            (!force && this.needsRender) ||
            !this.tilemapMetaProvider ||
            entity.location.x < 0 ||
            entity.location.y < 0
        ) {
            return
        }

        // Delegate camera positioning to camera management service
        if (this.features.camera) this.cameraManagementService.updatePlayerLocation(entity, force)
    }

    screenShakeImpulse = (entity: Entity, amplitude: number, durationMs: number) => {
        if (this.features.camera) this.cameraManagementService.screenShakeImpulse(entity, amplitude, durationMs)
    }

    screenShake = (entity: Entity) => {
        if (this.features.camera) this.cameraManagementService.screenShake(entity)
    }

    // ============================================================================
    // EFFECTS AND OVERLAYS METHODS
    // ============================================================================

    updateTileEffects = (wind: WindLeavesConfig | null = null, center?: Coordinate, storm = 0, brightness?: number) =>
        this.tileEffectSystem.update(wind, center, storm, brightness)

    predictTreeStrike = (location: Coordinate, entity: Entity) => {
        const target = this.tilemapMetaProvider?.getEntityTileAt(location.x, location.y)
        if (target?.entityTile === "testore" && this.rockMass.active) return
        if (!target || Mechanics.entityTile.hpDecrementOnHit(target.entityTile, entity.weapon?.weaponType || "none") < 1) return
        this.tileEffectSystem.predictTreeStrike({
            effectType: "damage", mapRoughCoordinates: location, ...target,
            surfaceId: this.tilemapMetaProvider.findMapDeltaAt(location.x, location.y)?.context?.surfaceId,
            strikerId: entity.entityId, strikeFrom: entity.location,
            strikeStrength: !entity.weapon || entity.weapon.weaponType === "none" ? 0.35 : entity.weapon.weaponType === "warhammer" ? 1.5 : 1,
        })
    }

    tileEffect = (effect: TileEffect) => {
        // An organic cave's rock has no block sprite to crack or crumble: its own layer shows the hit.
        if (this.rockMass.tileEffect(effect)) return
        this.tileEffectSystem.applyTileEffect(effect)
    }

    overlay = () => this.worldOverlaySurface

    setStaticHidingAllowed = (allowed: boolean) => this.skyManager.setStaticHidingAllowed(allowed)
    setProjectionFloor = (floor: number) => { this.projectionFloor = floor }

    /**
     * The roof stands a storey higher than the storey it caps, so seen from above it should recede by one
     * storey less than the storey does (scene_room_renderer.ts storeyTone): `gain`, per channel, lifts it
     * back that far through the storey's own tone. Undefined: no lift.
     */
    setRoofTone = (gain?: readonly [number, number, number]): void => {
        const key = gain ? gain.map(g => g.toFixed(3)).join(",") : undefined
        if (key === this.roofToneKey) return
        this.roofToneKey = key
        if (!gain) {
            this.houseRoof.filters = null
            return
        }
        this.roofTone ||= new filters.ColorMatrixFilter()
        const [r, g, b] = gain
        this.roofTone.matrix = [r, 0, 0, 0, 0, 0, g, 0, 0, 0, 0, 0, b, 0, 0, 0, 0, 0, 1, 0]
        this.houseRoof.filters = [this.roofTone]
    }

    get worldProjectionOffset(): number { return this.surface.pivot.y }

    private updateHouseProjection = (): void => {
        // Choose the world origin when loading the active map. Adding the first stairs
        // introduces house metadata mid-room; changing the pivot then jumps the camera.
        // Passive floors inherit the active world's lift through their parent surface.
        const offset = this.projectionFloor === undefined && this.tilemapMetaProvider?.getHousePresentation()
            ? TileSize : 0
        this.surface.pivot.y = offset
        this.worldOverlaySurface.pivot.y = offset
    }

    // ============================================================================
    // MINIMAP METHODS
    // ============================================================================

    updateMinimap = (tilemapMetaProvider: TileMapMetaProvider) => {
        if (this.features.minimap) this.miniMapSurface.update(tilemapMetaProvider)
    }

    updateBiomeColorMap = (biomeCoordinate: Coordinate, color: number) => {
        if (this.features.minimap) this.miniMapSurface.updateBiomeColorMap(biomeCoordinate, color)
    }

    removeLocationIcon = (iconId: string) => {
        this.miniMapSurface.removeLocationIcon(iconId)
    }

    // ============================================================================
    // STATE MANAGEMENT METHODS
    // ============================================================================

    update = (tilemapMetaProvider: TileMapMetaProvider, visibility?: Visibility, levelHandOff = false): boolean => {
        if (tilemapMetaProvider && levelHandOff && this.handOverTo(tilemapMetaProvider, visibility)) return true
        if (tilemapMetaProvider) {
            const previousHouse = this.tilemapMetaProvider?.twoStoreyHouse
            const nextHouse = tilemapMetaProvider.twoStoreyHouse
            const linkedFloor = isRelatedFloorTransfer(this.roomId, previousHouse, tilemapMetaProvider.roomId, nextHouse)
            const replacedMap = this.tilemapMetaProvider !== tilemapMetaProvider
            this.tilemapMetaProvider = tilemapMetaProvider
            this.updateHouseProjection()
            // Restart can replace the active map under the same room ID, without
            // sending companion frames when the new camp has no building.
            if (this.roomId !== tilemapMetaProvider.roomId || replacedMap) {
                if (linkedFloor) this.sceneRenderer.activateRoom(tilemapMetaProvider.roomId)
                else this.sceneRenderer.clear()
                this.houseRoofFade.reset()
            }
            const house = tilemapMetaProvider.getHousePresentation()
            this.houseRoofCells = new Set(hasHouseRoof(house) ? roofCellsForHouse(house).map(cellKey) : [])
            this.houseRoofFallback.clear()
            if (hasHouseRoof(house)) {
                buildHouseRoof(roofCellsForHouse(house), tilemapMetaProvider.width(), tilemapMetaProvider.height(), tilemapMetaProvider.roomId)
                    .filter(entry => entry.layerId === "sky").forEach(entry => this.houseRoofFallback.set(`${entry.x},${entry.y}`,
                        String(tileSetSpecs[entry.tileSpec.tilesetId].firstgid + entry.tileSpec.tilePos)))
            }
            this.houseRoof.y = hasHouseRoof(house) ? houseRoofOffset(house, this.projectionFloor ?? house.floor) : 0
            this.houseRoof.zIndex = this.houseRoofZIndex(house)
            this.houseRoofDepth.y = this.houseRoofShade.y = this.houseRoof.y
            this.houseRoofDepth.zIndex = this.houseRoof.zIndex - 0.5
            this.houseRoofShade.zIndex = this.houseRoof.zIndex + 0.01
            this.roofCastShadow.zIndex = this.houseRoof.zIndex + 0.02
            // Update composite tile renderer with new tilemap meta provider
            this.compositeTileRenderer.updateTilemapMetaProvider(tilemapMetaProvider)
            // Update tile effect system with new tilemap meta provider
            this.tileEffectSystem.updateTilemapMetaProvider(tilemapMetaProvider)
            // Update sky manager with new tilemap meta provider
            this.skyManager.updateTilemapMetaProvider(tilemapMetaProvider)
            if (this.roomId !== tilemapMetaProvider.roomId) {
                this.lavaTileFade.clear()
                this.clearLandingArt()
                this.roomId = tilemapMetaProvider.roomId
                // new room boundary has been crossed
                this.tileEffectSystem.clearSurfaceObjects()
                this.RANDOM_NUMBER_GENERATOR = seedrandom(this.roomId)
                this.rngCache.clear()
                // Reset sky state
                if (!linkedFloor) this.skyManager.resetSkyState()
                // Update tile effect system with new room context
                this.tileEffectSystem.updateHoleEffectContext(this.roomId, this.RANDOM_NUMBER_GENERATOR)
            }
            this.rerender()
            if (this.features.camera) this.cameraManagementService.updateTranslationComputer(tilemapMetaProvider)
        }
        this.visibility = visibility
        if (this.visibility) {
            this.visibility.lastUpdateTs = Date.now()
        }
        return false
    }

    keepsLevelsDrawn = (): boolean => !!this.onHandOff
    drawnFloorProvider = (roomId: string): TileMapMetaProvider | undefined => this.sceneRenderer.floorProvider(roomId)

    // ============================================================================
    // CHANGING LEVEL WITHOUT A REPAINT
    // ============================================================================

    /**
     * A change of level inside a house, to a floor this client already draws as a passive floor:
     * that floor's map becomes the active one where it stands, and this one goes on drawing the floor
     * just left. Repainting both from scratch (the destination here, the floor left as a new passive
     * floor) took tens of milliseconds in one frame on every stair and every fall.
     */
    private handOverTo = (provider: TileMapMetaProvider, visibility?: Visibility): boolean => {
        const leaving = this.tilemapMetaProvider
        if (!this.onHandOff || !leaving || this.isRendering || this.roomId === provider.roomId ||
            !isRelatedFloorTransfer(this.roomId, leaving.twoStoreyHouse, provider.roomId, provider.twoStoreyHouse)) return false
        const next = this.sceneRenderer.floorMap(provider.roomId)
        if (!(next instanceof MapRenderingManager) || !next.tilemapMetaProvider || next.isRendering) return false
        const exchanged = this.sceneRenderer.exchange(provider.roomId, leaving, this, (root, sky) =>
            this.handOver(next, { surface: root, skySurface: sky, worldOverlaySurface: new WorldOverlaySurface(this.logic, root) },
                provider.twoStoreyHouse.floor))
        if (!exchanged) return false
        next.adoptProvider(provider, visibility)
        next.onHandOff?.(next)
        return true
    }

    /** Swap roles with `next`, each keeping what it has drawn; this map takes the passive floor's surfaces. */
    private handOver = (next: MapRenderingManager, passive: Pick<MapRole, "surface" | "skySurface" | "worldOverlaySurface">,
        activeFloor: number): void => {
        const mine = this.ownedArt(), theirs = next.ownedArt()
        const active = this.role()
        this.takeRole({ ...next.role(), ...passive, features: OTHER_FLOOR_FEATURES, grassSway: undefined,
            projectionFloor: activeFloor, onHandOff: undefined }, mine)
        next.takeRole(active, theirs)
    }

    private role = (): MapRole => ({
        surface: this.surface, skySurface: this.skySurface, worldOverlaySurface: this.worldOverlaySurface,
        miniMapSurface: this.miniMapSurface, sceneRenderer: this.sceneRenderer,
        cameraManagementService: this.cameraManagementService, screenShakeManager: this.screenShakeManager,
        grassSway: this.grassSway, features: this.features,
        projectionFloor: this.projectionFloor, onHandOff: this.onHandOff,
    })

    private takeRole = (role: MapRole, art: DisplayObject[]): void => {
        ;({ surface: this.surface, skySurface: this.skySurface, worldOverlaySurface: this.worldOverlaySurface,
            miniMapSurface: this.miniMapSurface, sceneRenderer: this.sceneRenderer,
            cameraManagementService: this.cameraManagementService, screenShakeManager: this.screenShakeManager,
            grassSway: this.grassSway, features: this.features,
            projectionFloor: this.projectionFloor, onHandOff: this.onHandOff } = role)
        moveChildren(art, this.surface)
        this.placeGroundRise()
        this.skyManager.moveTo(this.skySurface)
        this.spriteManagementService.setDefaultSurface(this.surface)
        this.compositeTileRenderer.setSurface(this.surface)
        this.tileEffectSystem.setSurfaces(this.surface, this.worldOverlaySurface)
        this.roofCompositeOverlap.setSurface(this.surface)
        this.sceneCompositeOverlap.setSurface(this.surface)
        this.sceneCompositeKey = undefined
        this.houseRoofFade.reset()
        this.updateHouseProjection()
        if (this.features.grassSway) this.registerGrassBlades()
        if (this.features.occluderFade) {
            if (typeof window !== "undefined") (window as any).occluderFade = this.occluderFade.config
        } else {
            // Nothing eases a passive floor's faded canopies back, and it borrows the active room's light.
            this.occluderFade.restoreAll()
            this.visibility = undefined
        }
    }

    /**
     * Everything this map put straight into its surface: what a full render tears down and rebuilds
     * (prepareRendering), plus build ghosts and roof restorations. Sprites inside its roof groups and
     * sky layers travel with those containers.
     */
    private ownedArt = (): DisplayObject[] => {
        const art = new Set<DisplayObject>([this.houseRoofDepth, this.houseRoof, this.houseRoofShade, this.wallHalo,
            this.floorOpeningEdges, this.buildingShadow, this.contactShadow, this.roofCastShadow, this.archShade,
            this.groundRise, this.rockMass,
            this.water,
            ...this.tilemapRendererList, ...this.lavaFadeSurfaces.values(),
            ...this.storeyEdges.displayObjects(),
            ...this.roofCompositeOverlap.getCopies(), ...this.sceneCompositeOverlap.getCopies()])
        this.roofGroups.forEach(({ surface }) => art.add(surface))
        this.spritedCellSprites.forEach(sprites => sprites.forEach(sprite => art.add(sprite)))
        // Shade bands lie beside their tree, not in it: left behind, they went down with the old storey's root.
        this.climbShades.forEach(shade => shade.displayObjects().forEach(sprite => art.add(sprite)))
        this.hatchSprites.forEach(hatch => {
            const panel = !hatch.destroyed && hatch.depthArtwork()
            if (panel) art.add(panel)
        })
        this.ownedGhosts.forEach(sprite => {
            if (sprite.destroyed || sprite.parent !== this.surface) this.ownedGhosts.delete(sprite)
            else art.add(sprite)
        })
        const pool = this.spriteManagementService.getSpritePool()
        for (let i = 0; i <= pool.ptr; i++) {
            const item = pool.items[i]
            if (item && !item.free && item.data) art.add(item.data)
        }
        return [...art].filter(child => !child.destroyed && child.parent === this.surface)
    }

    /** Offer this floor's vegetation to the wind; it was drawn while another floor had it. */
    private registerGrassBlades = (): void => {
        const sway = this.grassSway
        if (!this.features.grassSway || !sway) return
        sway.detachAll()
        const pool = this.spriteManagementService.getSpritePool()
        for (let i = 0; i <= pool.ptr; i++) {
            const item = pool.items[i]
            const blade = item && !item.free && !item.data?.destroyed ? (item.data as any).grassBlade : undefined
            if (blade) sway.register(item.data, blade[0], blade[1], blade[2], blade[3], blade[4])
        }
    }

    /**
     * The live room's provider for the floor this map already draws. Same content, so no repaint:
     * the links to the storeys above and below carry over until the companion frames relink them.
     */
    private adoptProvider = (provider: TileMapMetaProvider, visibility?: Visibility): void => {
        const drawn = this.tilemapMetaProvider
        provider.setUpperFloorProvider(drawn.getUpperFloorProvider())
        provider.setFloorSupportProvider(drawn.getFloorSupportProvider())
        this.tilemapMetaProvider = provider
        this.compositeTileRenderer.updateTilemapMetaProvider(provider)
        this.tileEffectSystem.updateTilemapMetaProvider(provider)
        this.skyManager.updateTilemapMetaProvider(provider)
        if (this.features.minimap) this.miniMapSurface.update(provider)
        if (this.features.camera) this.cameraManagementService.updateTranslationComputer(provider)
        this.visibility = visibility
        if (this.visibility) this.visibility.lastUpdateTs = Date.now()
    }

    /**
     * Does the map still owe the screen a paint? True from the moment a room (or a redraw) is handed
     * over until the next frame has drawn it. The room-reveal hold asks, because the ground is the
     * one thing that is certainly not ready on the frame a room lands (see RoomReveal).
     */
    hasPendingRender = (): boolean => {
        // The storeys round this one are drawn behind the curtain too (SceneFloorRenderer prepaint).
        return this.needsRender || this.pendingRerender || (this.features.camera && this.sceneRenderer.hasUnpaintedFloors())
    }

    /** False while this map has its room's tiles but has not drawn them (a hidden storey, before its first reveal). */
    hasPainted = (): boolean => !this.tilemapMetaProvider || this.paintedRoomId === this.roomId

    rerender = () => {
        if (this.isRendering) {
            // Queue the re-render request for after current rendering completes
            this.pendingRerender = true
        } else {
            this.needsRender = true
        }
    }

    reset = () => {
        this.cameraManagementService.reset()
    }

    /**
     * Clean up resources and cancel pending operations
     */
    public destroy = (): void => {
        this.hatchSprites.forEach(hatch => { if (!hatch.destroyed) hatch.destroy({ children: true }) })
        this.hatchSprites.clear()
        this.renderedOcclusionTiles.clear()
        this.sceneRenderer.clear()
        this.roofCompositeOverlap.clear()
        this.sceneCompositeOverlap.clear()
        this.clearRoofGroups()
        this.houseRoof.destroy({ children: true })
        this.houseRoofDepth.destroy()
        this.houseRoofShade.destroy()
        this.wallHalo.destroy()
        this.floorOpeningEdges.destroy()
        this.storeyEdges.destroy()
        this.buildingShadow.destroy()
        this.contactShadow.destroy()
        this.roofCastShadow.destroy()
        this.archShade.destroy()
        this.climbShades.forEach(shade => shade.destroy())
        this.climbShades.clear()
        this.groundRise.destroy()
        this.rockMass.destroy()
        this.water.destroy({ children: true })
        if (this.shadeRoomId !== undefined) withdrawBuildingShade(this.shadeRoomId, this)
        this.lavaTileFade.clear()
        this.lavaFadeSurfaces.forEach(surface => surface.destroy({ children: true }))
        this.lavaFadeSurfaces.clear()
        this.clearLandingArt()
        // Clean up sprite management service
        this.spriteManagementService.destroy()
    }

    // ============================================================================
    // UTILITY/HELPER METHODS
    // ============================================================================

    randomIntBetweenRng = (min: number, max: number, seed?: string) => {
        let randomFloat
        if (!seed) {
            randomFloat = this.RANDOM_NUMBER_GENERATOR ? this.RANDOM_NUMBER_GENERATOR() : Math.random()
        } else {
            const toUse = `${seed}-${min}-${max}-${this.roomId}`
            randomFloat = this.rngCache.get(toUse)
            if (randomFloat === undefined) {
                randomFloat = this.RANDOM_NUMBER_GENERATOR ? this.RANDOM_NUMBER_GENERATOR() : Math.random()
                this.rngCache.set(toUse, randomFloat)
            }
        }

        return Math.floor(randomFloat * (max - min + 1) + min)
    }

    pctChance = (pct: number, seed?: string) => {
        // min and max included
        return this.randomIntBetweenRng(0, 100, seed) >= 100 - pct
    }

    pertubation = (tileId: string, x: number, y: number, offset: number, type: string, range: number = 5) => {
        const key = `${type}-${tileId}-${x}-${y}-${range}-${offset}`
        return this.randomIntBetweenRng(-range, range, key)
    }

    // ============================================================================
    // PRIVATE HELPER METHODS
    // ============================================================================

    private shouldRender = (): boolean => {
        return this.needsRender && !!this.tilemapMetaProvider
    }

    private prepareRendering = (): void => {
        this.roofCompositeOverlap.reset()
        this.sceneCompositeOverlap.reset()
        this.sceneCompositeKey = undefined
        this.roofArtwork.clear()
        this.renderedOcclusionTiles.clear()
        this.houseRoof.clear()
        this.houseRoofDepth.clear()
        this.houseRoofShade.clear()
        this.tileEffectSystem.clearSurfaceObjects()
        let errorCount = 0
        const releasedCount = this.spriteManagementService.releaseAllSprites(spriteItemObject => {
            const sprite = spriteItemObject.data
            if (!sprite.parent) {
                spriteItemObject.data = new Sprite()
                errorCount++
                return true
            } else {
                sprite.parent?.removeChild(sprite)
                return false
            }
        })
        this.clearRoofGroups()

        // Sprited-layer cells use OWNED sprites (not the frame pool), so releaseAllSprites above does
        // not touch them -- destroy them here before the full rebuild. children:true tears down any
        // damage-overlay children; texture defaults false so the shared spritesheet textures survive.
        this.spritedCellSprites.forEach(sprites =>
            sprites.forEach(s => {
                s.parent?.removeChild(s)
                s.destroy({ children: true })
            }),
        )
        this.spritedCellSprites.clear()
        this.wallTrimAtlas.reset()
        this.stackedLadders.clear()
        this.spritedLayerIds.clear()
        this.spritedCellSurfaceId.clear()
        // A full rebuild releases every pooled sprite, which is every blade the wind was bending -- and
        // a released sprite is not merely dead, it is about to be handed to some other tile. Per-CELL
        // removals need no such hook (GrassSway prunes destroyed sprites itself), but discovering a
        // whole room's worth one frame at a time would mean bending sprites that now belong elsewhere.
        this.grassSway?.clear()
        this.compositeTileRenderer.clearBonecallerStones()
        // A full render repaints every cell, so any pending incremental dirty cells are subsumed.
        this.dirtyCells.clear()
        this.dirtyNeedsFull = false

        // Clear tilemap renderers
        this.lavaTileFade.clearSprites()
        this.tilemapRendererList.forEach(renderer => renderer.clear())
        this.skyManager.clearSkySurfaces()
        this.tilemapRenderers.reset()

        // Clear tile effect system surface objects
        this.tileEffectSystem.clearSurfaceObjects()

        // Composite-tile textures (TileTextureExtractor.textureCache / compositeTileSpriteBounds and
        // this.compositeTileSpriteTextures) are content-addressed by `${tilesetId}-${compositeTileId}`,
        // immutable, and safely shared across rooms, so they are an intentional PERSISTENT memo --
        // NOT cleared per rerender (regenerating each rerender was a hitch) and bounded by the number
        // of distinct composite-tile types in the loaded tilesets. These were previously ".clear()"ed
        // here, but those calls were silent no-ops -- the Maps were accessed with bracket syntax, so
        // .clear() never touched the entries; the access is now .get/.set. Do NOT clear+destroy them
        // on room change: live sprites can still reference a cached texture, and destroy() nulls
        // Texture.orig, crashing any later getBounds() ("Cannot read properties of null (reading
        // 'width')").
    }

    private renderAllLayers = (): void => {
        this.groundLayerRenderers.clear()
        this.archedCells = new Set(this.wallSeamCells().arched)
        this.skyTilemaps = []
        this.renderedWithBuildingShadow = !!buildingShadowConfig().enabled
        this.renderHouseRoof()
        this.houseRoofDepth.soften()
        const { visit } = this.tilemapMetaProvider

        visit(({ tilemap, sky, sprited, mapSpriteCaps = [], spriteFragments, index, layerId }) => {
            if (spriteFragments) {
                spriteFragments.forEach(fragment => {
                    this.tileEffectSystem.renderSpriteFragment(fragment)
                })
            } else if (sprited) {
                this.renderSpritedLayer(tilemap, mapSpriteCaps, index, layerId)
            } else {
                if (tilemap) {
                    const tilemapRender = sky
                        ? this.skyManager.getSkyTilemapRenderer()
                        : this.tilemapRenderers.next().value
                    if (sky) this.skyTilemaps.push(tilemap)
                    else if (layerId) this.groundLayerRenderers.set(layerId, tilemapRender)
                    for (let y = 0; y < this.tilemapMetaProvider.height(); y++) {
                        for (let x = 0; x < this.tilemapMetaProvider.width(); x++) {
                            this.renderAt(x, y, tilemap, sky ? "sky" : undefined, {
                                tilemapRender,
                                fadeLayerId: layerId,
                            })
                        }
                    }
                }
            }
        })
        this.renderWallLintels()
        this.renderedWallSeams = this.wallSeamSignature()
        this.renderedWallTrims = this.wallTrimSignature()
    }

    private roofGroupAt(x: number, y: number): { surface: Container; tiles: CompositeTilemap } {
        const cell = `${x},${y}`
        const groupKey = this.roofGroupByCell.get(cell)
        if (groupKey) return this.roofGroups.get(groupKey)
        const sky = this.tilemapMetaProvider.resolveTileMapFromLayerId("sky")
        const pending = [{ x, y }]
        this.roofGroupByCell.set(cell, cell)
        let bottom = y + 1
        for (let i = 0; i < pending.length; i++) {
            const at = pending[i]
            bottom = Math.max(bottom, at.y + 1)
            for (const next of [{ x: at.x - 1, y: at.y }, { x: at.x + 1, y: at.y },
                { x: at.x, y: at.y - 1 }, { x: at.x, y: at.y + 1 }]) {
                const key = `${next.x},${next.y}`
                const tile = sky?.[next.x]?.[next.y]
                if (!tile || tile === "0" || this.houseRoofCells.has(key) || this.roofGroupByCell.has(key) ||
                    this.tilemapMetaProvider.isHiddenTile(tile)) continue
                this.roofGroupByCell.set(key, cell)
                pending.push(next)
            }
        }
        const surface = new Container()
        const tiles = new NormalBlendTilemap()
        surface.addChild(tiles)
        // A connected ceiling must cover every supporting wall as a unit.
        // Its height moves the artwork, while ground depth orders it against
        // trunks and bodies outside the southern edge.
        surface.zIndex = roofGroundDepth(bottom - 1)
        surface.visible = this.skySurface.visible
        surface.alpha = this.skyManager.getSkyAlpha()
        this.surface.addChild(surface)
        const group = { surface, tiles }
        this.roofGroups.set(cell, group)
        return group
    }

    private clearRoofGroups(): void {
        this.roofGroups.forEach(({ surface, tiles }) => {
            // Pooled tile-effect sprites are owned by SpriteManagementService.
            surface.removeChildren()
            tiles.destroy({ children: true })
            surface.destroy()
        })
        this.roofGroups.clear()
        this.roofGroupByCell.clear()
    }

    /** This storey's roof cells the storey above covers, as a comparable key. */
    private roofCover(): string {
        const provider = this.tilemapMetaProvider
        if (!provider) return ""
        return [...this.houseRoofCells].filter(cell => {
            const [x, y] = cell.split(",").map(Number)
            return provider.isRoofCoveredAt(x, y)
        }).join(";")
    }

    private houseRoofZIndex(house?: TwoStoreyHouse): number {
        if (!hasHouseRoof(house)) return 0
        // Use only the roof footprint, independently of floor extensions.
        // Elevation changes the art position; supporting walls share ground depth.
        const bottom = roofCellsForHouse(house).reduce((edge, cell) => Math.max(edge, cell.y + 1), 0)
        return roofGroundDepth(bottom - 1)
    }

    private recordRoofArtwork(owner: Container, bounds: Rectangle): void {
        let cells = this.roofArtwork.get(owner)
        if (!cells) this.roofArtwork.set(owner, cells = [])
        cells.push(bounds)
    }

    private renderHouseRoof = (): void => {
        const house = this.tilemapMetaProvider.getHousePresentation()
        if (!hasHouseRoof(house)) return
        const sky = this.tilemapMetaProvider.resolveTileMapFromLayerId("sky")
        const cells = new Map(roofCellsForHouse(house).map(cell => [cellKey(cell), cell]))
        // Live roof extensions also sit at this storey's ceiling height and support the level above.
        sky?.forEach((column, x) => column.forEach((tile, y) => {
            if (tile && tile !== "0") cells.set(`${x},${y}`, { x, y })
        }))
        this.houseRoofCells = new Set(cells.keys())
        this.renderedRoofCover = this.roofCover()
        const painted = new Map<string, { x: number; y: number; tile: string }>()
        for (const { x, y } of cells.values()) {
            if (this.tilemapMetaProvider.getMapDeltaAt(x, y, "sky")?.removed) continue
            // Under the storey above, the roof is its ceiling: that floor is drawn over it, and the
            // roof would only show as a sliver beneath the upper storey's front edge.
            if (this.tilemapMetaProvider.isRoofCoveredAt(x, y)) continue
            const roofTile = sky?.[x]?.[y]
            // Older prototype camps had an empty sky layer or floor art in it. Reconstruct
            // their roof with the manual builder rather than painting floor tiles overhead.
            const tile = roofTile && roofTile !== "0" && this.tilemapMetaProvider.resolveTileSet(roofTile)?.tilesetId !== "floors"
                ? roofTile : this.houseRoofFallback.get(`${x},${y}`)
            if (!tile || tile === "0") continue
            painted.set(`${x},${y}`, { x, y, tile })
        }
        for (const { x, y, tile } of painted.values()) {
            const texture = contentTexture(this.tilemapMetaProvider.resolveTilesetTileId(tile))
            this.houseRoof.tile(texture, x * TileSize, y * TileSize)
            const tileset = this.tilemapMetaProvider.resolveTileSet(tile)
            const [left, top, right, bottom] = roofVisibleBounds(tileset?.tilesetId, Number(tile) - tileset?.firstGid)
            const px = x * TileSize + left, py = y * TileSize + top
            const width = right - left, height = bottom - top
            this.recordRoofArtwork(this.houseRoof, new Rectangle(px, py + this.houseRoof.y, width, height))
            this.renderedOcclusionTiles.add(this.houseRoof, new Rectangle(px, py, width, height))
            // A drop shadow under the roof, only while the sun's building shadows are off: it leans
            // down and to the right, against the sun everything else casts with.
            if (!this.renderedWithBuildingShadow) this.houseRoofDepth.beginFill(0x151018, 0.17)
                .drawRect(px + 4, py + 6, width, height).endFill()
            // The exposed south face gives the roof thickness; never draw seams
            // across connected interior tiles. Respect the wooden art's alpha bounds.
            if (bottom < TileSize || !painted.has(`${x},${y + 1}`)) {
                this.houseRoofDepth.beginFill(0x302019, 0.65)
                    .drawRect(px, py + height, width, 3).endFill()
            }
        }
        this.shadeRoofSlopes(painted)
    }

    /**
     * The roof lit as a roof, multiplied over its art (houseRoofShade): a pitched roof's long front slope
     * darkens from under the ridge down to its eave, every eave's last rows darker again, and the verges
     * at its sides a little; past its edges, a drip of shade into the slots under its eave and beside its verges.
     */
    private shadeRoofSlopes = (painted: Map<string, { x: number; y: number; tile: string }>): void => {
        const provider = this.tilemapMetaProvider
        const has = (x: number, y: number) => painted.has(`${x},${y}`)
        const pitched = this.pitchedRoofTileIds()
        const art = (tile: string) => {
            const tileset = provider.resolveTileSet(tile)
            const [left, top, right, bottom] = roofVisibleBounds(tileset?.tilesetId, Number(tile) - tileset?.firstGid)
            return { left, top, right, bottom }
        }
        const gray = (v: number) => {
            const c = Math.max(0, Math.min(255, Math.round(255 * v)))
            return (c << 16) | (c << 8) | c
        }
        for (const { x, y, tile } of painted.values()) {
            let top = y, bottom = y
            while (has(x, top - 1)) top--
            while (has(x, bottom + 1)) bottom++
            const { left, top: artTop, right, bottom: artBottom } = art(tile)
            const px = x * TileSize + left, width = right - left
            const eave = bottom * TileSize + art(painted.get(`${x},${bottom}`).tile).bottom
            // The ridge as pitchedRoofPiece draws it: on the top row, or a row down when the whole ridge drops there.
            const slope = pitched.has(provider.resolveTilesetTileId(tile))
            const drops = slope && bottom - top >= 4 && pitchedRoofPiece((cx, cy) => has(cx, cy), x, top).startsWith("back.")
            const ridge = top * TileSize + (drops ? TileSize : 0) + ROOF_SHADE.ridge
            const west = !has(x - 1, y), east = !has(x + 1, y)
            for (let row = y * TileSize + artTop; row < y * TileSize + artBottom; row += 2) {
                const front = slope ? Math.max(0, Math.min(1, (row - ridge) / Math.max(1, eave - ridge))) : 0
                let v = 1 - ROOF_SHADE.front * Math.pow(front, 1.3)
                if (row >= eave - ROOF_SHADE.eaveRows) v *= ROOF_SHADE.eave
                if (v >= 0.995 && !west && !east) continue
                const verge = ROOF_SHADE.vergeWidth
                this.houseRoofShade.beginFill(gray(v), 1).drawRect(px, row, width, 2).endFill()
                if (west) this.houseRoofShade.beginFill(gray(ROOF_SHADE.verge), 1).drawRect(px, row, verge, 2).endFill()
                if (east) this.houseRoofShade.beginFill(gray(ROOF_SHADE.verge), 1).drawRect(px + width - verge, row, verge, 2).endFill()
            }
            // The drip: shade dropped past the roof's edge into the slot below the eave, and beside the verges.
            const ease = (t: number) => t * t * (3 - 2 * t)
            if (!has(x, y + 1)) for (let d = 0; d < ROOF_SHADE.dripDepth; d += 2) {
                const v = 1 - (1 - ROOF_SHADE.drip) * (1 - ease(d / ROOF_SHADE.dripDepth))
                this.houseRoofShade.beginFill(gray(v), 1).drawRect(px, y * TileSize + artBottom + d, width, 2).endFill()
            }
            for (const [side, dir] of [[west, -1], [east, 1]] as [boolean, number][]) {
                if (!side) continue
                for (let d = 0; d < ROOF_SHADE.sideWidth; d += 2) {
                    const v = 1 - (1 - ROOF_SHADE.side) * (1 - ease(d / ROOF_SHADE.sideWidth))
                    const sx = dir < 0 ? px - d - 2 : px + width + d
                    this.houseRoofShade.beginFill(gray(v), 1).drawRect(sx, y * TileSize + artTop, 2, artBottom - artTop).endFill()
                }
            }
        }
    }

    /**
     * The sun's shadows on this storey: cast by its own walls and roofs and by everything on the
     * storeys stacked above it, cut to where this storey can be stood on (building_shadow_geometry.ts),
     * and by the trees and bushes growing on it (foliage_shadow.ts).
     * Recomputed when any of those storeys is edited, re-baked only when the shadow itself changed;
     * its strength follows the daylight every frame. Tune live with window.buildingShadow and
     * window.foliageShadow.
     */
    /**
     * Keeps the raised-ground step directly above the map1 tilemap: above the grass it is cut into,
     * below map2's flowers, dirt edges and floors. Same zIndex as the tilemaps, so the order is the place.
     */
    private placeGroundRise = (): void => {
        const map1 = this.groundLayerRenderers.get("map1")
        if (!map1 || map1.parent !== this.surface) {
            if (this.groundRise.parent !== this.surface) this.surface.addChild(this.groundRise)
            return
        }
        const at = this.surface.getChildIndex(map1) + 1
        if (this.groundRise.parent === this.surface && this.surface.getChildIndex(this.groundRise) === at) return
        if (this.groundRise.parent) this.groundRise.parent.removeChild(this.groundRise)
        this.surface.addChildAt(this.groundRise, this.surface.getChildIndex(map1) + 1)
    }

    /** Keeps the water directly above the raised-ground step, which is directly above map1. */
    private placeWater = (): void => {
        if (this.groundRise.parent !== this.surface) {
            if (this.water.parent !== this.surface) this.surface.addChild(this.water)
            return
        }
        const at = this.surface.getChildIndex(this.groundRise) + 1
        if (this.water.parent === this.surface && this.surface.getChildIndex(this.water) === at) return
        if (this.water.parent) this.water.parent.removeChild(this.water)
        this.surface.addChildAt(this.water, this.surface.getChildIndex(this.groundRise) + 1)
    }

    /**
     * Once a frame, with every body that stands somewhere: legs clipped and ringed where they wade, frames
     * reflected where they stand over the water. Returns who is wading.
     */
    updateWater = (contacts: WaterContact[], daylight?: number): Set<string> => {
        let renderer: Renderer | undefined
        try {
            renderer = getRendererApp().renderer as Renderer
        } catch {
            renderer = undefined // no Application (a headless test): no reflections, waders still cut
        }
        return this.water.update(contacts, this.surface, renderer, daylight ?? 1)
    }

    /** px of water at a world point, undefined on dry land. */
    waterDepthAt = (px: number, py: number): number | undefined => this.water.depthAt(px, py)

    private updateBuildingShadow = (): void => {
        const config = buildingShadowConfig()
        // The old roof drop shadow stands in while these are off: a toggle repaints the roof.
        if (this.renderedWithBuildingShadow !== undefined && this.renderedWithBuildingShadow !== !!config.enabled) {
            this.renderedWithBuildingShadow = undefined
            this.rerender()
        }
        const provider = this.tilemapMetaProvider
        if (provider?.roomId !== this.shadeRoomId) {
            if (this.shadeRoomId !== undefined) withdrawBuildingShade(this.shadeRoomId, this)
            this.shadeRoomId = provider?.roomId
        }
        if (!config.enabled || !provider) {
            this.buildingShadow.visible = false
            this.contactShadow.visible = false
            this.roofCastShadow.visible = false
            this.wallHalo.visible = false
            this.archShade.visible = false
            this.shadeSprites(0, BUILDING_SHADOW.faces, 0x000000)
            if (provider) setBuildingShadeAlpha(provider.roomId, 0)
            return
        }
        const storeys: TileMapMetaProvider[] = []
        for (let p: TileMapMetaProvider | undefined = provider; p && !storeys.includes(p); p = p.getUpperFloorProvider()) storeys.push(p)
        // The sun by day, the moon by night: the whole world re-casts the other way when they swap (unseen, both faded out).
        const light = shadowLight(this.brightness(), this.underSky())
        const sun = { x: light.x, y: light.y }
        this.shadowSun = sun
        const indoors = config.indoors !== false
        const softness = config.softness ?? 1.25
        const color = config.color ?? BUILDING_SHADOW.color
        const contactAlpha = config.contact ?? BUILDING_SHADOW.contact
        const contactColor = config.contactColor ?? BUILDING_SHADOW.contactColor
        const contactReach = config.contactReach ?? BUILDING_SHADOW.contactReach
        const contactSoftness = config.contactSoftness ?? BUILDING_SHADOW.contactSoftness
        const foliage = foliageShadowConfig()
        const foliageDepth = foliage.depth ?? 0.5
        const silhouettes = foliage.silhouettes !== false
        const grassStrength = foliage.grass ?? FOLIAGE_SHADOW.grass
        // The storey below's roofs are walked on wherever this storey has no floor: they take its shadows too.
        const below = provider.twoStoreyHouse?.floor > 0 ? provider.getFloorSupportProvider() : undefined
        const version = [below, ...storeys].map(p => p ? `${p.roomId}:${p.getPresentationVersion()}` : "").join("|") +
            `|${STOREY_RISE_PX}:${ROOF_UPLIFT_PX}|${sun.x}:${sun.y}|${indoors}:${softness}:${color}|${contactReach}:${contactSoftness}:${contactColor}` +
            `|${shadowBakeResolution()}|${this.houseRoofCells.size}` +
            `|${!!foliage.enabled}:${foliageDepth}:${silhouettes}:${grassStrength}`
        // A plant whose art was still loading is looked for again, now and then.
        if (this.foliageRetryAt !== undefined && performance.now() >= this.foliageRetryAt) this.checkedShadowVersion = undefined
        // Re-cast in turn with the frame's other heavy work (frame_work_budget.ts): a frame late, the shade of a wall just
        // laid is not missed; the first cast, or a retry for art that was loading, does not wait.
        if (version !== this.checkedShadowVersion && (this.checkedShadowVersion === undefined || claimHeavyWork())) {
            this.checkedShadowVersion = version
            const stack = storeys.map(this.shadowStorey)
            if (below) stack[0] = { ...stack[0], roofsBelow: this.shadowStorey(below).roofs }
            const shadowOptions = { sun, rise: STOREY_RISE_PX, roofLift: ROOF_UPLIFT_PX, indoors }
            // The shade on the house roof below is this storey's (bodies walk on it), but that storey draws it (roofCastShadow).
            const belowHouse = below?.getHousePresentation()
            const roofApart = hasHouseRoof(belowHouse) ? { cells: new Set(roofCellsForHouse(belowHouse).map(cellKey)), out: [] as ShadowPolygon[] } : undefined
            const castKey = `${shadowStackKey(stack)}|${stack[0].floor}|${sun.x}:${sun.y}|${STOREY_RISE_PX}:${ROOF_UPLIFT_PX}|${indoors}|` +
                (roofApart ? [...roofApart.cells].join(";") : "-")
            let cast = this.castBuildings
            if (cast?.key !== castKey) {
                const polygons = castBuildingShadows(stack, stack[0].floor, shadowOptions, roofApart)
                cast = this.castBuildings = { key: castKey, polygons, apart: roofApart?.out ?? [],
                    posts: elevatedPostShadows(stack, stack[0].floor, shadowOptions) }
            } else if (roofApart) {
                roofApart.out.push(...cast.apart)
            }
            // Copied: the plants' and the roof-apart polygons are added to it below.
            const polygons = [...cast.polygons]
            const drawnApart = new Set(roofApart?.out)
            polygons.push(...drawnApart)
            // The buildings' own come first: tall art takes only theirs (shadeSprites).
            const buildingPolygons = polygons.length
            this.foliageRetryAt = undefined
            let stamps: ShadowStamp[] = []
            let grass: ShadowStamp[] = []
            let feet: ShadowPolygon[] = []
            if (foliage.enabled) {
                const plants = foliageShadows(provider, { sun, depth: foliageDepth, silhouettes, grass: grassStrength },
                    this.compositeTileRenderer.denseCopyLifts)
                polygons.push(...plants.polygons)
                stamps = plants.stamps
                grass = plants.grass
                feet = plants.feet
                if (plants.pending) this.foliageRetryAt = performance.now() + 500
            }
            // Furniture casts with the plants: its shade is published, so a body by a table is in it.
            // Indoors (unless window.buildingShadow.indoors), under a roof or the floor above: no sun to cast with.
            const over = stack[1]
            const indoorCells = indoors ? undefined : new Set([...stack[0].roofs, ...(over?.slab ?? []), ...(over?.surface ?? [])])
            this.wallCover = indoorCells
            // A room seen through an arch cut over its doorway here: the storey below's floor just inside is indoors, out
            // of the sun's shade, and showed lit through the vault where a gate's passage shows shaded.
            this.archShade.clear()
            if (below && stack[0].undrawn) {
                const lower = this.shadowStorey(below)
                const inside = indoorShadeCells([lower, ...stack], lower.floor, shadowOptions)
                stack[0].undrawn.forEach(cell => {
                    const [x, y] = cell.split(",").map(Number)
                    if (inside.has(`${x},${y - 1}`)) this.archShade.beginFill(config.archColor ?? BUILDING_SHADOW.archColor, 1)
                        .drawRect(x * TileSize, y * TileSize, TileSize, TileSize).endFill()
                })
            }
            const furniture = furnitureShadows(provider, sun, indoorCells ? (x, y) => indoorCells.has(`${x},${y}`) : undefined)
            stamps = stamps.concat(furniture.stamps)
            feet = feet.concat(furniture.feet)
            if (furniture.pending) this.foliageRetryAt = performance.now() + 500
            const contact = castContactShadows(stack, stack[0].floor, shadowOptions, contactReach, feet)
            const contactGeometry = `${contactSoftness}|${contactColor}|${polygonsKey(contact)}`
            if (contactGeometry !== this.renderedContactGeometry) {
                this.renderedContactGeometry = contactGeometry
                this.contactPolygons = contact
                this.contactShadow.draw(contact, contactSoftness, [], contactColor)
            }
            publishBuildingShade(provider.roomId, this, polygons, stamps, color, buildingPolygons)
            // Drawn with them, but nobody stands in it: upper posts' shadows on their own level.
            const drawn = polygons.filter(polygon => !drawnApart.has(polygon)).concat(cast.posts)
            // Most edits (a tree felled, grass cut) leave every shadow as it was: bake only on a change.
            // Long grass's shade is drawn too, after the rest (over a full shadow it changes nothing), but not published.
            const geometry = `${softness}|${color}|${polygonsKey(drawn)}|${stampsKey(stamps)}|${stampsKey(grass)}`
            if (geometry !== this.renderedShadowGeometry) {
                this.renderedShadowGeometry = geometry
                this.shadowPolygons = drawn
                this.shadowStamps = stamps
                this.grassStamps = grass
                // Stamped silhouettes cannot be cut like the polygons: they are masked to the floor when baked.
                this.buildingShadow.draw(drawn, softness, [...stamps, ...grass], color,
                    sunReceivingRects(stack, stack[0].floor, shadowOptions))
            }
            // This storey's house roof, under what the storeys above cast across the ground round it.
            const roofKey = [...this.houseRoofCells].join(";")
            if (cast.roofKey !== roofKey) {
                cast.roofKey = roofKey
                cast.roof = this.houseRoofCells.size ? castRoofShadows(stack, stack[0].floor, this.houseRoofCells, shadowOptions) : []
            }
            const roofShade = cast.roof ?? []
            const roofGeometry = `${softness}|${color}|${polygonsKey(roofShade)}`
            if (roofGeometry !== this.renderedRoofShadowGeometry) {
                this.renderedRoofShadowGeometry = roofGeometry
                this.roofShadowPolygons = roofShade
                this.roofCastShadow.draw(roofShade, softness, [], color)
            }
        }
        this.buildingShadow.alpha = (config.alpha ?? BUILDING_SHADOW.alpha) * light.strength
        this.buildingShadow.visible = this.buildingShadow.alpha > 0 &&
            (this.shadowPolygons.length > 0 || this.shadowStamps.length > 0 || this.grassStamps.length > 0)
        // The shade's strength, shapes or not: bodies cast their own shadows by it (lpc_composite_sprite).
        setBuildingShadeAlpha(provider.roomId, this.buildingShadow.alpha, sun)
        this.contactShadow.alpha = contactAlpha * light.strength
        this.contactShadow.visible = this.contactShadow.alpha > 0 && this.contactPolygons.length > 0
        this.archShade.alpha = (config.arch ?? BUILDING_SHADOW.arch) * light.strength
        this.archShade.visible = this.archShade.alpha > 0 && this.archShade.geometry.graphicsData.length > 0
        this.roofCastShadow.alpha = this.buildingShadow.alpha * this.houseRoof.alpha
        this.roofCastShadow.visible = this.houseRoof.visible && this.roofCastShadow.alpha > 0 && this.roofShadowPolygons.length > 0
        // By the shade's strength whether or not this storey has a shadow of its own: a wall's south face is out of the
        // sun on every storey, and a tower's closed middle storeys (no shadows) drawn with untinted faces between
        // tinted ones banded its face storey by storey. Only sprites standing in a shadow take more than the faces' tint.
        this.shadeSprites(this.buildingShadow.alpha, config.faces ?? BUILDING_SHADOW.faces, color, config.sprites !== false)
        // The walls' halo: rebuilt when the walls' art changes, re-baked only where it changed.
        const haloKey = `${this.artworkVersion}|${shadowBakeResolution()}`
        // A re-bake takes its turn with the frame's other heavy work (frame_work_budget.ts): after a storey is drawn again
        // its halo follows a frame or two later. The first bake in a room does not wait.
        if (haloKey !== this.renderedHaloKey && (this.renderedHaloKey === undefined || claimHeavyWork())) {
            this.renderedHaloKey = haloKey
            const pieces: HaloPiece[] = []
            const raised = (provider.twoStoreyHouse?.floor ?? 0) > 0
            for (const [cellKey, sprites] of this.spritedCellSprites) for (const sprite of sprites) {
                if (!(sprite as WallFaceSprite).wallArt || sprite.destroyed || !sprite.visible || !sprite.texture?.valid) continue
                // Above the ground a south face spills its halo only onto a floor of its own storey in front of it.
                // With none there (a tower's outside face) the spill fell on the face of the storey below, a dark band
                // at every storey of the tower by day.
                if (raised && (sprite as WallFaceSprite).wallFace) {
                    const [x, y] = cellKey.split("-").map(Number)
                    if (!storeyFloored(provider, x, y + 1)) continue
                }
                pieces.push({ texture: sprite.texture, x: sprite.x, y: sprite.y, width: sprite.texture.width, height: sprite.texture.height })
            }
            this.wallHalo.draw(pieces, HALO.wall.softness, HALO.wall.spread)
        }
        this.wallHalo.alpha = (config.halo ?? HALO.wall.alpha) * light.strength
        this.wallHalo.visible = this.wallHalo.alpha > 0 && this.wallHalo.groupCount > 0
    }

    /**
     * The sprites drawn over the shade layer, shaded as the ground they stand on (sprite_shade.ts): walls,
     * furniture and low entity tiles in a shadow take its tint; every wall's south face takes the faces'
     * shade too. Which sprites stand in shade is worked out again only when the shadows or the art change;
     * the tints follow the shadows' strength (daylight).
     */
    private shadeSprites = (strength: number, faces: number, color: number, sprites = true): void => {
        const provider = this.tilemapMetaProvider
        const key = `${this.renderedShadowGeometry}|${this.artworkVersion}|${this.surfaceObjectsByKey.size}|${this.shadowSun.x}:${this.shadowSun.y}`
        if (provider && key !== this.shadedSpritesKey) {
            this.shadedSpritesKey = key
            this.shadedTints = undefined
            const before = new Set(this.shadedSprites.map(entry => entry.sprite))
            const roomId = provider.roomId, sun = this.shadowSun
            const entries: ShadedSprite[] = []
            const share = (points: { x: number; y: number }[]) =>
                points.reduce((hits, p) => hits + (inBuildingShade(roomId, p.x, p.y) ? 1 : 0), 0) / points.length
            // Furniture casts now: it looks for its shade sunward of its piece's foot, past its own shadow.
            const pieces = new Map(furniturePieces(provider).map(piece => [`${piece.parent.x},${piece.parent.y}`, piece]))
            // How far into shade each sprite is drawn now, by where it stands: a full render makes every
            // sprite afresh, and a sprite under a felled tree should still ease out of its shade.
            const shown = this.shadeShown
            this.shadeShown = new Map()
            const add = (spriteKey: string, sprite: Sprite, face: boolean, target: number, line?: number) => {
                const was = shown.get(spriteKey)
                if (target <= 0 && !face && !(was > 0)) return
                entries.push({ key: spriteKey, sprite, face, target, shown: was ?? (this.shadedSpritesSeen ? 0 : target), line })
            }
            const owners = new Map<string, () => boolean>()
            this.spritedCellSprites.forEach((cellSprites, cellKey) => {
                const [cx, cy, layerId] = cellKey.split("-")
                const x = Number(cx), y = Number(cy)
                const parent = provider.getMapDeltaAt(x, y, layerId)?.context?.parent ?? { x, y }
                cellSprites.forEach((sprite, index) => {
                    // Art drawn on the floor, under the shade layer (a stair down), is shaded by the layer itself.
                    if (sprite.destroyed || !(sprite instanceof Sprite) || sprite.zIndex < 50) return
                    const wall = !!(sprite as WallFaceSprite).wallArt
                    const piece = layerId === "sprite2" ? pieces.get(`${parent.x},${parent.y}`) : undefined
                    // A wall piece takes the buildings' shade only (sprite_shade.ts wallInShade). Not one under its own
                    // building's cover: the cover keeps its foot out of the sun's shade, so only a piece whose sample point
                    // fell just outside (an outer end beside a shaded corridor, the jamb of a shaded gate) went dark, a
                    // square on a lit face. Its face takes the faces' tint like every other.
                    const target = wall && this.wallCover?.has(`${parent.x},${parent.y}`) ? 0 : wall
                        ? (wallInShade(parent.x, parent.y, (px, py, only) => inBuildingShade(roomId, px, py, only), sun) ? 1 : 0)
                        : share(piece
                            ? artShadePoints(piece.left * TileSize, piece.top * TileSize, (piece.right - piece.left + 1) * TileSize,
                                Math.min(SHADED_ART_MAX_HEIGHT, (piece.bottom - piece.top + 1) * TileSize), sun, true)
                            : furnitureShadePoints(parent.x, parent.y))
                    add(`${cellKey}#${index}`, sprite, !!(sprite as WallFaceSprite).wallFace, target)
                })
            })
            this.surfaceObjectsByKey.forEach((object, objectKey) => {
                if (!object.compositeTileId) return
                object.spriteSet.forEach((sprite, index) => {
                    if (sprite.destroyed) return
                    const width = Math.abs(sprite.width), height = Math.abs(sprite.height)
                    const casts = compositeCasts(object.tilesetId, object.compositeTileId) ?? true
                    const left = sprite.x - sprite.anchor.x * width, top = sprite.y - sprite.anchor.y * height
                    const points = artShadePoints(left, top, width, height, sun, casts)
                    if (points) {
                        add(`${objectKey}#${index}`, sprite, false, share(points))
                        return
                    }
                    // Tall art (a tree) is shaded only as high as the buildings' shade climbs it.
                    const climb = shadeClimb({ x: left + width / 2, y: top + height - 4 }, height, sun,
                        (px, py) => inBuildingShade(roomId, px, py, true))
                    const spriteKey = `${objectKey}#${index}`
                    owners.set(spriteKey, () => this.surfaceObjectsByKey.get(objectKey)?.spriteSet[index] === sprite)
                    add(spriteKey, sprite, false, climb > 0 ? 1 : 0,
                        climb > 0 ? (height - 4 - climb) / (Math.abs(sprite.scale.y) || 1) : undefined)
                })
            })
            // Each climbing shade kept while its art and line are, else made afresh.
            const climbShades = new Map<string, ClimbShade>()
            this.climbing = entries.filter(entry => entry.line !== undefined)
            for (const entry of this.climbing) {
                const kept = this.climbShades.get(entry.key)
                if (kept?.isFor(entry.sprite, entry.line)) {
                    this.climbShades.delete(entry.key)
                    climbShades.set(entry.key, kept)
                    continue
                }
                const made = ClimbShade.create(entry.sprite, entry.line, owners.get(entry.key) ?? (() => false))
                if (made) climbShades.set(entry.key, made)
            }
            this.climbShades.forEach(shade => shade.destroy())
            this.climbShades = climbShades
            // Sprites first seen with the room take their shade at once; later changes ease.
            this.shadedSpritesSeen = true
            entries.forEach(({ sprite }) => before.delete(sprite))
            before.forEach(sprite => { if (!sprite.destroyed) sprite.tint = 0xffffff })
            this.shadedSprites = entries
            this.shadeEasing = entries.some(entry => entry.shown !== entry.target)
        }
        // Ease every sprite toward its shade over a shadow fade's length (baked_groups.ts).
        const now = performance.now(), dt = Math.min(100, now - (this.shadeEasedAt ?? now))
        this.shadeEasedAt = now
        let eased = false
        if (this.shadeEasing) {
            const step = dt / (2 * SHADE_FADE_MS)
            let still = false
            for (const entry of this.shadedSprites) {
                if (entry.shown === entry.target) continue
                entry.shown = entry.shown < entry.target ? Math.min(entry.target, entry.shown + step) : Math.max(entry.target, entry.shown - step)
                eased = true
                still = still || entry.shown !== entry.target
            }
            this.shadeEasing = still
        }
        this.shadedSprites.forEach(entry => { if (entry.shown > 0) this.shadeShown.set(entry.key, entry.shown) })
        // Climbing shades follow their art (sway, fall, fade) every frame.
        for (const entry of this.climbing) this.climbShades.get(entry.key)?.sync(sprites ? strength * entry.shown : 0, color)
        const faceTint = faceShadeTint(faces, strength)
        const tints = `${faceTint}|${sprites ? Math.round(strength * 100) : 0}|${color}`
        if (tints === this.shadedTints && !eased) return
        this.shadedTints = tints
        for (const { sprite, face, shown, line } of this.shadedSprites) {
            if (sprite.destroyed) continue
            // Tall art keeps its own light: its shade is drawn over it, as high as it climbs.
            const shadeT = sprites && shown > 0 && line === undefined ? shadeTint(0xffffff, strength * shown, color) : 0xffffff
            sprite.tint = face ? multiplyTints(faceTint, shadeT) : shadeT
        }
    }

    /** This storey as its shadows see it: walls, floor, openings and roofs (and which roofs are pitched). */
    private shadowStorey = (p: TileMapMetaProvider): ShadowStorey => {
        const floor = p.twoStoreyHouse?.floor ?? 0
        const walls = new Set<string>(), surface = new Set<string>(), slab = new Set<string>()
        const roofs = new Set<string>(), pitched = new Set<string>()
        p.getMapDeltas().forEach(delta => {
            if (delta.layerId !== "sprite") return
            // A wall's own cell, or a piece of a run standing on its cell though the builder parents it to the wall
            // below (the side of a room above its corner): either way a wall stands there and casts.
            const piece = delta.blocking && !delta.removed && !delta.context?.previewTarget &&
                ItemTypeCharacteristics[delta.context?.itemType]?.buildType === "wall"
            if (piece || p.wallItemAt(delta.x, delta.y)) walls.add(`${delta.x},${delta.y}`)
        })
        const below = p.getFloorSupportProvider()
        const undrawn = new Set<string>()
        p.resolveTileMapFromLayerId("map2")?.forEach((column, x) => column?.forEach((tile, y) => {
            if (!tile || tile === "0" || p.isFloorOpeningAt(x, y)) return
            surface.add(`${x},${y}`)
            if (!p.isStairAt(x, y)) slab.add(`${x},${y}`)
            // Under an arch over a gateway below the floor is not drawn (the passage shows through it): no shade lands
            // there, though the vault still shades the passage and the floor round it is drawn as before.
            if (walls.has(`${x},${y}`) && archOpening(below, p, x, y)) undrawn.add(`${x},${y}`)
        }))
        const pitchedTiles = this.pitchedRoofTileIds()
        p.resolveTileMapFromLayerId("sky")?.forEach((column, x) => column?.forEach((tile, y) => {
            if (!tile || tile === "0" || p.isHiddenTile(tile) || p.getMapDeltaAt(x, y, "sky")?.removed) return
            roofs.add(`${x},${y}`)
            if (pitchedTiles.has(p.resolveTilesetTileId(tile))) pitched.add(`${x},${y}`)
        }))
        // Materials that cast other than wall-stone: a castle wall's thick body, merlons' free blocks. Merlons (a
        // parapet) stand on the wall below with no floor; the top they leave open between them takes their shade.
        const profiles = new Map<string, NonNullable<WallAutotileDefinition["shadow"]>>()
        const parapets = new Set<string>()
        walls.forEach(cell => {
            const [x, y] = cell.split(",").map(Number)
            const definition = this.wallDefinitionFor(p.wallItemAt(x, y))
            if (definition?.shadow) profiles.set(cell, definition.shadow)
            if (definition?.parapet && !surface.has(cell) && below?.wallItemAt(x, y)) parapets.add(cell)
        })
        return { floor, ground: floor === 0, walls, surface, slab, roofs, pitched, ...(profiles.size ? { profiles } : {}),
            ...(parapets.size ? { parapets } : {}), ...(undrawn.size ? { undrawn } : {}) }
    }

    /** Every tile a pitched roof material draws (its pieces and ornaments), as tileset tile ids. */
    private pitchedRoofTileIds = (): Set<string> => {
        if (this.pitchedRoofTiles?.source !== ROOF_AUTOTILE_DEFINITIONS) {
            const tiles = new Set<string>()
            ROOF_AUTOTILE_DEFINITIONS.forEach(definition => {
                if (!definition.pitched) return
                const add = (tilePos: number) => tiles.add(`${definition.tilesetId}-${tilePos + 1}`)
                Object.values(definition.pitched).forEach(add)
                Object.values(definition.ornaments ?? {}).forEach(ornament => Object.values(ornament.pieces ?? {}).forEach(add))
            })
            this.pitchedRoofTiles = { source: ROOF_AUTOTILE_DEFINITIONS, tiles }
        }
        return this.pitchedRoofTiles.tiles
    }

    /** The room's brightness (1 by day). A storey drawn from another one shares the active room's. */
    private brightness = (): number | undefined =>
        this.visibility?.darkness ?? (this.logic as any)?.clientRenderer?.map?.()?.getVisibility?.()?.darkness

    /** Whether the room has a sky for the moon to light: a day cycle, and not a cave's darkness. */
    private underSky = (): boolean => {
        const logic = this.logic as any
        const visibility = this.visibility ?? logic?.clientRenderer?.map?.()?.getVisibility?.()
        return logic?.scene?.daylight !== undefined && !visibility?.fullOcclusion
    }

    /** The shadow polygons on this storey, in its own pixels (for tests and for shading bodies). */
    getBuildingShadowPolygons = (): readonly ShadowPolygon[] => this.shadowPolygons

    /** The plants' silhouettes stamped with those polygons, in the same pixels. */
    getBuildingShadowStamps = (): readonly ShadowStamp[] => this.shadowStamps

    private finalizeRendering = (): void => {
        this.compositeSprites = this.liveComposites()
        this.artworkVersion++
        this.rebuildRoofComposites()
        if (this.features.minimap) this.miniMapSurface.update(this.tilemapMetaProvider)
        this.needsRender = false
        this.tileEffectSystem.applyDamageVisualizationToExistingTiles(this.surfaceObjectsByKey)
        this.paintedRoomId = this.roomId
    }

    private liveComposites = (): Sprite[] => [...this.surfaceObjectsByKey.values()].filter(object => !!object.compositeTileId)
        .flatMap(object => object.spriteSet)

    private rebuildRoofComposites(): void {
        this.roofCompositeOverlap.rebuild(this.liveComposites().filter(sprite => !sprite.destroyed),
            [...this.roofArtwork].map(([owner, bounds]) => ({ owner, bounds })))
    }

    /** The roofs and walls this storey draws, which lower storeys' trees can stand in front of (its own pixels). */
    getCeilingArtwork = (): RoofArtwork[] => {
        const artwork: RoofArtwork[] = [...this.roofArtwork].map(([owner, bounds]) => ({ owner, bounds }))
        for (const sprites of this.spritedCellSprites.values()) for (const sprite of sprites) {
            artwork.push({ owner: sprite, upright: true, bounds: [new Rectangle(sprite.x, sprite.y, sprite.width, sprite.height)] })
        }
        return artwork
    }

    private syncSceneComposites(): void {
        const scene = this.sceneRenderer.backgroundCompositeArtwork(this.tilemapMetaProvider?.getHousePresentation())
        const key = `${this.artworkVersion}:${scene.key}`
        if (key !== this.sceneCompositeKey || this.sceneCompositeOverlap.isStale()) {
            const level = this.tilemapMetaProvider?.twoStoreyHouse?.floor
            const artwork = [...this.getCeilingArtwork().map(art => ({ ...art, level })), ...scene.ceilings]
            this.sceneCompositeOverlap.rebuild(scene.composites, artwork)
            this.sceneCompositeKey = key
        }
        this.sceneCompositeOverlap.sync()
    }

    // Hand the map the wind that bends its grass. Set once by the renderer at construction; the map
    // passes it down to the composite tile renderer, which is what actually builds the grass, and
    // tells it when a full rebuild has taken every blade away.
    setGrassSway = (grassSway: GrassSway) => {
        this.grassSway = grassSway
    }

    // Render one sprited-layer cell as an OWNED sprite (not from the frame pool) and track it per-cell
    // in spritedCellSprites so it can be removed/repainted individually (incremental redraw). Shared
    // spritesheet textures (Texture.from) are reused; only the Sprite wrapper is owned here.
    private renderSpritedCell = (x: number, y: number, tile: string, layerId: string, index: number): void => {
        const tilesetTileId = this.tilemapMetaProvider.resolveTilesetTileId(tile)
        if (tile === "0" || !tile || this.tilemapMetaProvider.isHiddenTile(tile) || tilesetTileId === "lpc-terrain-723") {
            return
        }
        const delta = this.tilemapMetaProvider.getMapDeltaAt(x, y, layerId)
        const context = delta?.context
        // What it looks like where storeys meet (a beam, a seamless face); it sorts and takes damage as the tile it is.
        const drawnTileId = layerId === "sprite" ? this.wallSeamTileId(x, y, tilesetTileId, context) : tilesetTileId
        const belowTile = this.tilemapMetaProvider.resolveTileMapFromLayerId(layerId)?.[x]?.[y + 1]
        const depthItem = { x, y, context, tilesetTileId,
            belowTilesetTileId: belowTile && belowTile !== "0" ? this.tilemapMetaProvider.resolveTilesetTileId(belowTile) : undefined,
            belowBlocking: this.tilemapMetaProvider.isCollisionAt(x, y + 1) }
        const item: CaptureGroup = { x, y, tile, context, delta: !!context?.hp ? delta : undefined }
        try {
            const fringed = wallGrassFringeTexture(contentTexture(drawnTileId), tilesetTileId,
                x, y, this.tilemapMetaProvider, context?.parent)
            const closed = layerId === "sprite" ? this.closeBareWallEdges(fringed, x, y, drawnTileId, context) : fringed
            const texture = layerId === "sprite" && context?.parent && this.tilemapMetaProvider.wallItemAt(context.parent.x, context.parent.y)
                ? withWallJointOverlap(closed, this.projectedWallJoints(x, y).map(p => ({ texture: contentTexture(p.id), dy: p.dy }))) : closed
            const adaptive = layerId === "sprite2" && stackedLadderState(this.tilemapMetaProvider, { x, y })
            // A wall of a material with trims: its cell baked with them, and what they draw past the cell.
            const trimmed = layerId === "sprite" && context?.parent && anyWallTrims()
                ? bakeWallCell(this.wallTrimAtlas, this.tilemapMetaProvider, this.wallSeamTileId, x, y, texture) : undefined
            const g = adaptive ? new StackedLadderSprite(this.tilemapMetaProvider, { x, y }) : new Sprite(trimmed?.texture ?? texture)
            if (g instanceof StackedLadderSprite) this.stackedLadders.add(g)
            this.surface.addChild(g)
            const fine = roughToFineCoordinates({ x, y })
            g.x = fine.x
            g.y = fine.y
            g.visible = !this.buildVeil.has(`${x}-${y}-${layerId}`) &&
                (this.tilemapMetaProvider.resolveTileMeta(tile).entityTileId !== "stairs-down" || this.downstairsHasSurface(x, y))
            // A descending stair is an opening in the floor; bodies always draw over it.
            g.zIndex = !adaptive && this.tilemapMetaProvider.resolveTileMeta(tile).entityTileId === "stairs-down"
                ? 0 : spritedTileZIndex(index, spritedTileAnchorY(depthItem))
            if (layerId === "sprite2") {
                this.orientStairSprite(g, x, y, tile)
                const hatch = hatchAt(this.tilemapMetaProvider, { x, y })
                const stairType = this.tilemapMetaProvider.resolveTileMeta(tile).entityTileId
                if (hatch && ["stairs-up", "stairs-down"].includes(stairType))
                    this.hatchSprites.add(attachHatchSprite(g, hatch, stairType === "stairs-up"))
                else if (!hatch && ["stairs-up", "stairs-down"].includes(stairType)) {
                    const brokenAt = context?.hatchBrokenAt
                    const age = getCurrentTime() - brokenAt
                    if (typeof brokenAt === "number" && age >= 0 && age < HATCH_BREAK_MS)
                        this.hatchSprites.add(attachHatchBreakSprite(g, brokenAt, stairType === "stairs-up"))
                }
            }

            const surfaceId = context?.surfaceId
            const isParent = context?.parent && context.parent?.x === x && context.parent?.y === y
            // A wall's south face turns from the sun (sun.ts: north-east); its top, drawn on the cell
            // north (a cap, parented to the cell below), and the long top of a north-south run catch it.
            // A covered seam (a storey stands on it) shows the face above running down.
            const wall = layerId === "sprite" && !!context?.parent && this.tilemapMetaProvider.wallItemAt(context.parent.x, context.parent.y)
            if (wall) (g as WallFaceSprite).wallArt = true
            // A parapet's piece is its own top as well as its face, and carries the face's shade in its art.
            if (wall && (isParent || drawnTileId !== tilesetTileId) && !this.wallDefinitionFor(wall)?.parapet) (g as WallFaceSprite).wallFace = true
            this.tileEffectSystem.processSurfaceId(isParent ? tilesetTileId : undefined, surfaceId, g, item.delta)

            const key = `${x}-${y}-${layerId}`
            this.artworkVersion++
            this.spritedLayerIds.add(layerId)
            const arr = this.spritedCellSprites.get(key)
            if (arr) {
                arr.push(g)
            } else {
                this.spritedCellSprites.set(key, [g])
            }
            // Trim art past the cell (rubble at the foot, a corner's spur): sorted as the wall, removed with it.
            trimmed?.overflow.forEach(o => {
                const spill = new Sprite(o.texture)
                spill.x = g.x + o.dx
                spill.y = g.y + o.dy
                spill.zIndex = g.zIndex
                spill.visible = g.visible
                this.surface.addChild(spill)
                this.spritedCellSprites.get(key)!.push(spill)
            })
            // Remember which surface this cell registered under so removeSpritedCell can detach these
            // exact sprites from the shared spriteSet on an incremental repaint.
            if (surfaceId) {
                this.spritedCellSurfaceId.set(key, surfaceId)
            }
        } catch (e) {
            logger.error({ tags: ["map", "error"] }, e)
        }
    }

    /**
     * A wall piece (or the top it carries) with its bare edges closed: where its art runs to the cell's edge and the
     * tile beside it on this storey has nothing there to meet it (effects/wall_bare_edges.ts). A tile whose image is
     * not loaded yet is left alone, and an unloaded neighbour counts as meeting it, so a line never appears wrongly.
     */
    private closeBareWallEdges = (texture: Texture, x: number, y: number, drawnTileId: string, context: any): Texture => {
        const provider = this.tilemapMetaProvider
        const parent = context?.parent
        const wall = ItemTypeCharacteristics[context?.itemType]?.buildType === "wall" || (parent && !!provider.wallItemAt(parent.x, parent.y))
        if (!wall) return texture
        const own = edgeProfile(contentTexture(drawnTileId))
        if (!own) return texture
        const tiles = provider.resolveTileMapFromLayerId("sprite")
        const meets: EdgeProfile = { ...own, opaque: { north: [], east: [], south: [], west: [] } }
        ;(["north", "east", "south", "west"] as const).forEach(side => (meets.opaque[side] = own.opaque[side].map(() => true)))
        const beside = (nx: number, ny: number): EdgeProfile | undefined => {
            const tile = tiles?.[nx]?.[ny]
            if (!tile || tile === "0" || provider.isHiddenTile(tile)) return undefined
            const id = provider.resolveTilesetTileId(tile)
            return edgeProfile(contentTexture(this.wallSeamTileId(nx, ny, id, provider.getMapDeltaAt(nx, ny, "sprite")?.context))) ?? meets
        }
        const bare = bareEdges(own, { north: beside(x, y - 1), east: beside(x + 1, y), south: beside(x, y + 1), west: beside(x - 1, y) })
        return withBareEdges(texture, bare, own.outline)
    }

    private renderSpritedLayer = (tilemap: TileMap, mapSpriteCaps: any[], index: number, layerId: string): void => {
        // The previous per-run buffer/mapSpriteCap "splitter" machinery was vestigial: it only
        // re-assigned each sprite's z-index to the SAME per-cell value it already had (a sprited tile
        // z-sorts by its own structure base row, context.parent.y -- see sprited_tile_zindex.ts). So
        // this is now a straight per-cell render via renderSpritedCell.
        const width = this.tilemapMetaProvider.width()
        const height = this.tilemapMetaProvider.height()
        for (let x = 0; x < width; x++) {
            for (let y = 0; y < height; y++) {
                this.renderSpritedCell(x, y, tilemap[x][y], layerId, index)
            }
        }
    }

    // ---------------------------------------------------------------------------------------------
    // Where walls of different storeys meet (WallSeams in game-common/room/autotile_wall.ts)
    // ---------------------------------------------------------------------------------------------

    private wallDefinitions?: { source: readonly WallAutotileDefinition[]; byItem: Map<string, WallAutotileDefinition> }
    private wallDefinitionFor = (itemType: string): WallAutotileDefinition | undefined => {
        if (this.wallDefinitions?.source !== WALL_AUTOTILE_DEFINITIONS) this.wallDefinitions = { source: WALL_AUTOTILE_DEFINITIONS,
            byItem: new Map(WALL_AUTOTILE_DEFINITIONS.filter(d => d.itemType).map(d => [d.itemType, d])) }
        return this.wallDefinitions.byItem.get(itemType)
    }

    /**
     * The piece a wall tile draws here: its top (on the row above its cell) under a wall of the storey
     * above is covered by a beam or more face; a seamless material's face standing on the same
     * material below drops its base. Anything else draws as authored.
     */
    private wallSeamTileId = (x: number, y: number, tilesetTileId: string, context: any, provider = this.tilemapMetaProvider): string => {
        const parent = context?.parent
        if (!parent || parent.x !== x || (y !== parent.y - 1 && y !== parent.y)) return tilesetTileId
        // A top piece carries its wall cell but not always its material: the wall standing there says.
        const itemType = context?.itemType ?? provider.wallItemAt(parent.x, parent.y)
        const definition = this.wallDefinitionFor(itemType)
        if (!definition?.seams || !tilesetTileId.startsWith(`${definition.tilesetId}-`)) return tilesetTileId
        const piece = wallSeamPiece(definition, Number(tilesetTileId.slice(definition.tilesetId.length + 1)) - 1,
            y === parent.y ? "face" : "top", coversWallBelow(this.wallDefinitionFor(provider.getUpperFloorProvider()?.wallItemAt(parent.x, parent.y))),
            provider.getFloorSupportProvider()?.wallItemAt(parent.x, parent.y) === itemType,
            !!provider.wallItemAt(parent.x, parent.y + 1))
        return piece ? `${piece.tilesetId}-${piece.tilePos + 1}` : tilesetTileId
    }

    /** Upper end faces projected onto this cell; paint in elevation order, never outside its wall art. */
    private projectedWallJoints = (x: number, y: number): { id: string; dy: number }[] => {
        const patches: { id: string; dy: number }[] = []
        const seen = new Set<TileMapMetaProvider>([this.tilemapMetaProvider])
        let level = 0
        const ceiling = this.wallJointCeiling()
        for (let upper = this.tilemapMetaProvider.getUpperFloorProvider(); upper && !seen.has(upper); upper = upper.getUpperFloorProvider()) {
            seen.add(upper); level++
            if ((upper.twoStoreyHouse?.floor ?? 0) > ceiling) continue
            const offset = level * STOREY_RISE_PX
            const start = Math.floor(y + offset / TileSize)
            for (let row = start; row <= Math.ceil(y + offset / TileSize); row++) {
                const item = upper.wallItemAt(x, row), definition = this.wallDefinitionFor(item)
                if (!definition?.seams?.joints) continue
                const delta = upper.getMapDeltaAt(x, row, "sprite")
                const tile = upper.resolveTileMapFromLayerId("sprite")?.[x]?.[row]
                if (!tile || !delta) continue
                const id = this.wallSeamTileId(x, row, upper.resolveTilesetTileId(tile), delta.context, upper)
                if (!Object.values(definition.seams.joints).some(ref => id === `${ref.tilesetId}-${ref.tilePos + 1}`)) continue
                patches.push({ id, dy: row * TileSize - offset - y * TileSize })
            }
        }
        return patches
    }

    /** Upstairs art disappears with the normal interior cutaway, including its overlap copies. */
    private wallJointCeiling = (): number => {
        const viewer = this.viewEntity ?? this.logic.playerEntity
        if (!viewer?.location || isExteriorFloorFall(viewer.floorFall)) return Infinity
        const seen = new Set<TileMapMetaProvider>()
        for (let p = this.tilemapMetaProvider; p && !seen.has(p); p = p.getUpperFloorProvider()) {
            seen.add(p)
            if (p.roomId !== viewer.location.roomId) continue
            const house = p.getHousePresentation()
            return house && insideHouse(house, viewer.location) ? house.floor : Infinity
        }
        return Infinity
    }

    /**
     * Where this storey's walls meet the storeys above and below: the wall cells under a wall above,
     * the doorways the storey above walls over (a gap in a run of a material with a lintel, under a
     * wall above), the walls standing on their own seamless material below, and those of them that arch
     * over a gateway of the storey below (their floor is not drawn: the passage shows through the arch).
     */
    private wallSeamCells = () => {
        const provider = this.tilemapMetaProvider
        const upper = provider?.getUpperFloorProvider(), lower = provider?.getFloorSupportProvider()
        const covered: string[] = [], continued: string[] = [], lintels: { x: number; y: number; itemType: string }[] = []
        const arched: string[] = []
        if (!provider || (!upper && !lower)) return { covered, continued, lintels, arched }
        const wallCells = (p: TileMapMetaProvider) => p.getMapDeltas().filter(d => d.layerId === "sprite" && p.wallItemAt(d.x, d.y))
        if (upper) wallCells(upper).forEach(({ x, y }) => {
            const own = provider.wallItemAt(x, y)
            if (own) {
                if (coversWallBelow(this.wallDefinitionFor(upper.wallItemAt(x, y)))) covered.push(`${x},${y}`)
                return
            }
            // The storey above cuts an arch over a gateway of its material's span: the arch is the lintel.
            if (archOpening(provider, upper, x, y)) return
            // Else a lintel spans the doorway: a gap in a run of this storey's walls, as wide as the storey above may wall
            // over (the server's GATEWAY_SPAN), in the material of the wall at its west jamb.
            const gap = (cx: number) => !provider.wallItemAt(cx, y) && !!upper.wallItemAt(cx, y)
            let x0 = x, x1 = x
            while (x - x0 < LINTEL_SPAN && gap(x0 - 1)) x0--
            while (x1 - x < LINTEL_SPAN && gap(x1 + 1)) x1++
            if (x1 - x0 + 1 > LINTEL_SPAN) return
            const west = provider.wallItemAt(x0 - 1, y), east = provider.wallItemAt(x1 + 1, y)
            const material = west && east ? this.wallDefinitionFor(west) : undefined
            if (material?.seams?.lintel) lintels.push({ x, y, itemType: west })
        })
        if (lower) wallCells(provider).forEach(({ x, y }) => {
            const own = provider.wallItemAt(x, y)
            const seams = this.wallDefinitionFor(own)?.seams
            if ((seams?.style === "seamless" || seams?.joints) && lower.wallItemAt(x, y) === own)
                continued.push(`${x},${y}${seams.joints ? `:${provider.wallItemAt(x, y + 1) ? "internal" : "end"}` : ""}`)
            if (archOpening(lower, provider, x, y)) arched.push(`${x},${y}`)
        })
        return { covered, continued, lintels, arched }
    }
    private wallSeamSignature = (): string => {
        const { covered, continued, lintels, arched } = this.wallSeamCells()
        const projected = this.tilemapMetaProvider.getMapDeltas().filter(d => d.layerId === "sprite" && !d.removed && d.context?.parent &&
            this.tilemapMetaProvider.wallItemAt(d.context.parent.x, d.context.parent.y))
            .map(d => `${d.x},${d.y}:${this.projectedWallJoints(d.x, d.y).map(p => `${p.id}@${p.dy}`).join(",")}`)
            .filter(k => !k.endsWith(":"))
        return [covered.sort().join(";"), continued.sort().join(";"), lintels.map(l => `${l.x},${l.y}`).sort().join(";"), projected.sort().join(";"),
            arched.sort().join(";")].join("|")
    }

    /**
     * The walls trims are drawn for, across the stack: every storey's wall pieces with their tiles, when any
     * material in it has trims. Empty when none does, so a stack without trims never repaints for them.
     */
    private wallTrimSignature = (): string => {
        const provider = this.tilemapMetaProvider
        if (!provider || !anyWallTrims()) return ""
        const { storeys } = storeyStack(provider)
        if (storeys.length < 2) return ""
        // Every storey of a stack asks for the same signature: the first to ask after a change works it out for them all.
        const version = `${storeys.map(p => `${p.roomId}:${p.getPresentationVersion()}`).join("|")}|${WALL_AUTOTILE_DEFINITIONS.length}`
        const shared = stackTrimSignatures.get(storeys[0])
        if (shared?.version === version && shared.definitions === WALL_AUTOTILE_DEFINITIONS) return shared.signature
        let trimmed = false
        const parts = storeys.map(p => p.getMapDeltas().filter(d => d.layerId === "sprite" && !d.removed && d.context?.parent).map(d => {
            const item = p.wallItemAt(d.context.parent.x, d.context.parent.y)
            if (!item) return ""
            if (!trimmed && this.wallDefinitionFor(item)?.trims) trimmed = true
            return `${d.x},${d.y}:${d.tileId ?? ""}:${item}`
        }).filter(Boolean).sort().join(";"))
        const signature = trimmed ? parts.join("|") : ""
        stackTrimSignatures.set(storeys[0], { version, definitions: WALL_AUTOTILE_DEFINITIONS, signature })
        return signature
    }

    /** A lintel over each doorway the storey above walls over, drawn as the top of the doorway's cell. */
    private renderWallLintels = (): void => {
        const index = this.spritedLayerIndex("sprite")
        this.wallSeamCells().lintels.forEach(({ x, y, itemType }) => {
            const lintel = this.wallDefinitionFor(itemType)?.seams?.lintel
            if (!lintel || y < 1) return
            try {
                const g = new Sprite(contentTexture(`${lintel.tilesetId}-${lintel.tilePos + 1}`))
                const fine = roughToFineCoordinates({ x, y: y - 1 })
                g.x = fine.x
                g.y = fine.y
                g.zIndex = spritedTileZIndex(index, y)
                this.surface.addChild(g)
                const key = `${x}-${y - 1}-sprite`
                this.spritedLayerIds.add("sprite")
                const cell = this.spritedCellSprites.get(key)
                if (cell) cell.push(g)
                else this.spritedCellSprites.set(key, [g])
                this.artworkVersion++
            } catch (e) {
                logger.error({ tags: ["map", "error"] }, e)
            }
        })
    }

    // The sprited-layer sprites currently drawn at one map cell, across every sprited layer. This is
    // how a tile-drawn entity gets a hover outline: the V2 chest is an invisible marker standing on
    // these sprites, so they -- not the marker -- are the shape to trace. Returns live sprites in the
    // map surface's own coordinate space; callers must not retain them across a rerender.
    getSpritedTilesAt = (x: number, y: number): Sprite[] => {
        const found: Sprite[] = []
        this.spritedLayerIds.forEach(layerId => {
            const sprites = this.spritedCellSprites.get(`${x}-${y}-${layerId}`)
            sprites?.forEach(sprite => {
                if (!sprite.destroyed) {
                    found.push(sprite)
                }
            })
        })
        return found
    }

    // The container those sprites live in, so an outline traced from them can be parented alongside
    // and share their coordinate space without any conversion.
    getTileSurface = (): Container => this.surface

    /**
     * The sprites drawing the sprited tile whose cell is (x, y): its own cell, and every nearby piece the builder
     * parented to it (a wall's top, drawn in the cell above). Another room's tile is looked up on the storey that
     * room is drawn as. Live sprites, so callers must not keep them across a rerender.
     */
    getTileArt = (x: number, y: number, layerId: string, roomId?: string): Sprite[] => {
        if (roomId && roomId !== this.roomId) return this.sceneRenderer.tileArt(roomId, x, y, layerId)
        const found: Sprite[] = []
        for (let cy = y - 2; cy <= y + 1; cy++) for (let cx = x - 1; cx <= x + 1; cx++) {
            if (cx !== x || cy !== y) {
                const parent = this.tilemapMetaProvider?.getMapDeltaAt(cx, cy, layerId)?.context?.parent
                if (parent?.x !== x || parent?.y !== y) continue
            }
            this.spritedCellSprites.get(`${cx}-${cy}-${layerId}`)?.forEach(sprite => {
                if (!sprite.destroyed && sprite.visible) found.push(sprite)
            })
        }
        return found
    }

    /** Every sprited layer's art for the tile at (x, y): what a wall looks like on screen, its top included. */
    getWallArt = (x: number, y: number): Sprite[] => {
        const found: Sprite[] = []
        this.spritedLayerIds.forEach(layerId => found.push(...this.getTileArt(x, y, layerId)))
        return found
    }

    // ---------------------------------------------------------------------------------------------
    // Build preview veil: an existing tile a build preview reshapes (a wall it joins, a roof edge
    // turning interior, a floor's trim) is hidden while the preview's ghost of its new shape stands in.
    // ---------------------------------------------------------------------------------------------

    /** Whether the tile drawn here can be hidden: a wall piece, a plain tile of a roof group, or a plain ground tile. */
    canVeilBuildCell = (x: number, y: number, layerId: string): boolean => {
        const maptype = this.tilemapMetaProvider?.resolveLayerMapping(layerId)?.maptype
        if (maptype === "sprited") return !!this.spritedCellSprites.get(`${x}-${y}-${layerId}`)?.some(sprite => !sprite.destroyed)
        if (maptype === "sky") return layerId === "sky" && this.roofGroupByCell.has(`${x},${y}`) &&
            !!this.plainLaidTile(x, y, this.tilemapMetaProvider.resolveTileMapFromLayerId("sky"), "sky")
        if (maptype === "tilemap") return this.groundLayerRenderers.has(layerId) &&
            !!this.plainLaidTile(x, y, this.tilemapMetaProvider.resolveTileMapFromLayerId(layerId))
        return false
    }

    /** Hide exactly these cells (`${x}-${y}-${layerId}`); every other one is drawn as the map has it. Takes effect at once. */
    setBuildVeil = (keys: Iterable<string>): void => {
        const next = new Set(keys)
        const changed = [...next].filter(key => !this.buildVeil.has(key)).concat([...this.buildVeil].filter(key => !next.has(key)))
        if (!changed.length) return
        this.buildVeil = next
        const groups = new Set<string>(), layers = new Set<string>()
        changed.forEach(key => {
            const dash = key.lastIndexOf("-")
            const layerId = key.slice(dash + 1)
            const [x, y] = key.slice(0, dash).split("-").map(Number)
            const maptype = this.tilemapMetaProvider?.resolveLayerMapping(layerId)?.maptype
            if (maptype === "sprited") {
                const tile = this.tilemapMetaProvider.resolveTileMapFromLayerId(layerId)?.[x]?.[y]
                const visible = !next.has(key) && (!tile ||
                    this.tilemapMetaProvider.resolveTileMeta(tile).entityTileId !== "stairs-down" || this.downstairsHasSurface(x, y))
                this.spritedCellSprites.get(key)?.forEach(sprite => { sprite.visible = visible })
            }
            else if (maptype === "sky" && this.roofGroupByCell.has(`${x},${y}`)) groups.add(this.roofGroupByCell.get(`${x},${y}`))
            else if (maptype === "tilemap") layers.add(layerId)
        })
        // A tilemap only appends or clears, so the layer or roof holding a changed cell is laid again.
        groups.forEach(this.relayRoofGroup)
        layers.forEach(this.relayGroundLayer)
    }

    /**
     * The texture a full render lays into a tilemap for this cell, or undefined where it lays none
     * (composite art, a hidden tile, the house roof, a collideable roof sprite).
     */
    private plainLaidTile = (x: number, y: number, tilemap: TileMap, sky?: "sky"): string | undefined => {
        const provider = this.tilemapMetaProvider
        const tile = tilemap?.[x]?.[y]
        if (!tile || !provider.shouldDrawTileAt(x, y, sky, tilemap) || provider.isHiddenTile(tile) || provider.getCompositeTileId(tile)) return
        if (sky && (tilemap === provider.resolveTileMapFromLayerId("sky") && this.houseRoofCells.has(`${x},${y}`) ||
            provider.getMapDeltaAt(x, y, "sky")?.collideable)) return
        return provider.resolveTilesetTileId(tile)
    }

    /** Lay one ground layer again, exactly as a full render lays it, without its veiled cells. */
    private relayGroundLayer = (layerId: string): void => {
        const renderer = this.groundLayerRenderers.get(layerId)
        const tilemap = this.tilemapMetaProvider?.resolveTileMapFromLayerId(layerId)
        if (!renderer || !tilemap) return
        renderer.clear()
        for (let y = 0; y < this.tilemapMetaProvider.height(); y++) {
            for (let x = 0; x < this.tilemapMetaProvider.width(); x++) {
                const texture = this.plainLaidTile(x, y, tilemap)
                if (texture) this.drawGroundTile(renderer, texture, x, y, layerId)
            }
        }
    }

    /** Lay one roof group's tiles again in full-render order (each sky layer, rows top down), without its veiled cells. */
    private relayRoofGroup = (groupKey: string): void => {
        const group = this.roofGroups.get(groupKey)
        if (!group) return
        const cells = [...this.roofGroupByCell].filter(([, key]) => key === groupKey)
            .map(([cell]) => cell.split(",").map(Number)).sort((a, b) => a[1] - b[1] || a[0] - b[0])
        const sky = this.tilemapMetaProvider.resolveTileMapFromLayerId("sky")
        group.tiles.clear()
        this.skyTilemaps.forEach(tilemap => cells.forEach(([x, y]) => {
            if (tilemap === sky && this.buildVeil.has(`${x}-${y}-sky`)) return
            const texture = this.plainLaidTile(x, y, tilemap, "sky")
            if (texture) group.tiles.tile(texture, x * TileSize, y * TileSize + houseRoofOffset())
        }))
    }

    // Remove (destroy) the owned sprites currently rendered for one sprited cell.
    private removeSpritedCell = (x: number, y: number, layerId: string): void => {
        const key = `${x}-${y}-${layerId}`
        const sprites = this.spritedCellSprites.get(key)
        if (!sprites) {
            return
        }
        // Detach these sprites from the shared surfaceObjectsByKey spriteSet BEFORE destroying them, so
        // the tile-effect system never keeps a destroyed sprite in a surface's set. A vertical wall's
        // cap+base cells share one surfaceId, so this removes only this cell's sprites and empties the
        // surface only once its last cell is removed.
        const surfaceId = this.spritedCellSurfaceId.get(key)
        if (surfaceId) {
            this.tileEffectSystem.detachSpritesFromSurface(surfaceId, sprites)
            this.spritedCellSurfaceId.delete(key)
        }
        sprites.forEach(s => {
            s.parent?.removeChild(s)
            if (!s.destroyed) {
                s.destroy({ children: true })
            }
        })
        this.spritedCellSprites.delete(key)
        this.artworkVersion++
    }

    // The z-index "index" a sprited layer renders with (matches renderAllLayers' visit()).
    private spritedLayerIndex = (layerId: string): number => (this.tilemapMetaProvider.resolveLayerMapping(layerId)?.idx ?? 0) + 1

    hasForegroundSceneFloor(playerZ: number): boolean {
        return this.sceneRenderer.hasForegroundFloor(playerZ)
    }

    isPlayerBehindScene(bounds: Rectangle, playerZ: number): boolean {
        return this.sceneRenderer.occludes(bounds, playerZ)
    }

    isPlayerHiddenByScene(bounds: Rectangle, playerZ: number): boolean {
        return this.sceneRenderer.covers(bounds, playerZ)
    }

    /** Test individual drawn tiles, preserving openings and irregular storey footprints. */
    overlapsRenderedMap(bounds: Rectangle): boolean {
        return this.renderedOcclusionTiles.overlaps(bounds) || this.findRenderedSprite(bounds, () => true)
    }

    /** Pushes the drawn tiles over `bounds` onto `out` as screen-space [left, top, right, bottom] runs. */
    collectRenderedTiles(bounds: Rectangle, out: number[]): void {
        this.renderedOcclusionTiles.collect(bounds, out)
    }

    /** Pushes the wall and tile sprites' art over `bounds` onto `out`, like collectRenderedTiles. */
    collectRenderedSprites(bounds: Rectangle, out: number[]): void {
        this.findRenderedSprite(bounds, art => {
            out.push(art.x, art.y, art.right, art.bottom)
            return false
        })
    }

    /** Visits the screen bounds of each live wall or tile sprite's art over `bounds` until `visit` returns true. */
    private findRenderedSprite(bounds: Rectangle, visit: (art: Rectangle) => boolean): boolean {
        const overlaps = (box: Rectangle) => box.x < bounds.right && box.right > bounds.x &&
            box.y < bounds.bottom && box.bottom > bounds.y
        const near = this.spriteBoundsFilter.reset(bounds)
        const hits = (sprite: Sprite) => {
            if (sprite.destroyed || !sprite.visible || !near.mayOverlap(sprite) || !sprite.worldVisible) return false
            const box = sprite.getBounds()
            if (!overlaps(box) || sprite.worldAlpha <= 0.15) return false
            const content = spriteContentWorldBounds(sprite, this.spriteOcclusionBounds)
            // Not read yet: clear until it is, rather than stalling this frame on the GPU.
            if (content === undefined) return false
            const art = content ?? box
            return overlaps(art) && visit(art)
        }
        for (const sprites of this.spritedCellSprites.values()) {
            if (sprites.some(hits)) return true
        }
        const pool = this.spriteManagementService.getSpritePool()
        for (let i = 0; i <= pool.ptr; i++) {
            const item = pool.items[i]
            if (item && !item.free && item.data && hits(item.data)) return true
        }
        return false
    }

    // Fade the tile art the LOCAL player is standing behind (tree canopies, a cave's ore blocks) toward
    // translucent, and ease the rest back to opaque. Called once per frame after entities are laid out.
    // playerZIndex mirrors Mechanics.entity.body.calculateZIndexBoost for the humanoid player
    // (100 + feet-y, defaultZIndex 0) -- the same key tile sprites are z-sorted against.
    //
    // Candidates are ONLY the pooled tile sprites carrying a fade policy (occluderFade, stamped by
    // CompositeTileRenderer from the tile's structured entityTileId via occluderFadePolicy). Ground
    // tiles are baked into CompositeTilemaps and never occlude; walls (owned sprited-cell sprites) are
    // deliberately excluded, so only the tagged tiles fade.
    applyOccluderFade = (player: Entity): void => {
        this.compositeTileRenderer.updateBonecallerStones()
        // Disabled (via the OCCLUDER_FADE_ENABLED constant or a runtime window.occluderFade.enabled
        // toggle) or no player yet: ease any residual fade back and do NO per-frame work (no pool scan).
        // The ticker calls render() every frame, including before a room's map/entities exist.
        if (!this.features.occluderFade || !this.occluderFade.config.enabled || !player?.location) {
            this.occluderFade.relaxAll()
            this.occluderCandidateBuf.length = 0 // hold no sprite refs while idle
            this.roofCompositeOverlap.sync()
            return
        }
        const feetX = player.location.x
        const feetY = player.location.y + BODY_GROUND_OFFSET
        this.occluderFade.update(feetX, feetY, Mechanics.entity.body.calculateZIndexBoost(player), this.collectOccluderCandidates())
        this.roofCompositeOverlap.sync()
    }

    // Reused buffer of the on-screen fade candidates, refilled each frame (never reallocated) so the
    // fade pass stays allocation-free. Scans only the pool's checked-out slots (0..ptr), not the whole
    // padded pool array; the tag is stamped by CompositeTileRenderer and cleared on pool checkout.
    private occluderCandidateBuf: Sprite[] = []
    private collectOccluderCandidates = (): Sprite[] => {
        const buf = this.occluderCandidateBuf
        buf.length = 0
        const pool = this.spriteManagementService.getSpritePool()
        const items = pool.items
        const ptr = pool.ptr
        for (let i = 0; i <= ptr; i++) {
            const item = items[i]
            if (item && !item.free && item.data && (item.data as any).occluderFade) {
                buf.push(item.data)
            }
        }
        return buf
    }

    // Called on a redrawClient signal (building/chest/door placement). Deferred to renderTileMap so it
    // runs after processPendingDeltas has applied the accompanying deltas. An applied ADD delta sets
    // pendingRedraw itself (see applyMapDelta), for the tiles that arrive without a signal.
    onRedraw = (): void => {
        this.pendingRedraw = true
    }

    // Repaint only the delta-changed cells. Sprited-layer cells are updated in place (remove old owned
    // sprites + re-render the current tile); if any dirty cell belongs to a tilemap/sky/other layer we
    // can't update in isolation (CompositeTilemap is append+clear only), so fall back to a full rerender.
    private repaintDirtyCells = (): void => {
        if (!this.tilemapMetaProvider || this.dirtyCells.size === 0) {
            this.dirtyCells.clear()
            this.dirtyNeedsFull = false
            return
        }
        // Fall back to a FULL rerender when: any dirty batch touched a removal/damaged tile (tile
        // effects -- see dirtyNeedsFull), OR any dirty cell is a non-sprited (tilemap/sky/occlusion)
        // layer that can't be updated in isolation (CompositeTilemap is append+clear only). Otherwise
        // every change is a pristine sprited placement and we repaint just those cells.
        let needFullRerender = this.dirtyNeedsFull
        this.dirtyNeedsFull = false
        this.dirtyCells.forEach(key => {
            const layerId = key.slice(key.lastIndexOf("-") + 1)
            if (this.tilemapMetaProvider.resolveLayerMapping(layerId)?.maptype !== "sprited") {
                needFullRerender = true
            }
        })
        if (needFullRerender) {
            this.dirtyCells.clear()
            this.rerender()
            return
        }
        this.dirtyCells.forEach(key => {
            const dash = key.lastIndexOf("-")
            const layerId = key.slice(dash + 1)
            const [x, y] = key
                .slice(0, dash)
                .split("-")
                .map(Number)
            this.removeSpritedCell(x, y, layerId)
            const tileMap = this.tilemapMetaProvider.resolveTileMapFromLayerId(layerId)
            const tile = tileMap?.[x]?.[y]
            if (tile) {
                this.renderSpritedCell(x, y, tile, layerId, this.spritedLayerIndex(layerId))
            }
        })
        this.dirtyCells.clear()
    }

    queueMapDelta = (delta: MapDelta[]) => {
        this.pendingMapDeltas = this.pendingMapDeltas.concat(delta)
    }

    /** Keep hidden companion collision/support current without painting its map. */
    flushMapDeltas = () => { this.processPendingDeltas() }

    private processPendingDeltas = () => {
        if (this.pendingMapDeltas.length > 0) {
            const deltas = [...this.pendingMapDeltas]
            this.pendingMapDeltas = []
            // Read before the batch applies: a piece is new if its cell held nothing of it before.
            const built = this.paintedRoomId === this.roomId ? this.newBuildPieces(deltas) : undefined

            deltas.forEach(delta => {
                this.applyMapDelta(delta)
            })
            if (built?.size) this.landBuiltPieces(built)
        }
    }

    private applyMapDelta = (delta: MapDelta) => {
        if (!delta || !this.tilemapMetaProvider) {
            return
        }

        const { x, y, blocking, occlusion, tileId, layerId, surfaceId, mapSpriteCap, collideable, temporary, context } =
            delta

        if (tileId) {
            // Remember this cell so a following redrawClient can repaint just it (see repaintDirtyCells).
            this.dirtyCells.add(`${x}-${y}-${layerId}`)
            // A wooden cap's recovered anchor depends on the blocking base below it.
            // Re-evaluate the cap when that base is placed or replaced incrementally.
            if (y > 0 && this.tilemapMetaProvider.resolveLayerMapping(layerId)?.maptype === "sprited") {
                this.dirtyCells.add(`${x}-${y - 1}-${layerId}`)
            }
            // A wall's bare edges are judged against the tiles beside it (see closeBareWallEdges).
            if (layerId === "sprite") {
                this.dirtyCells.add(`${x - 1}-${y}-${layerId}`)
                this.dirtyCells.add(`${x + 1}-${y}-${layerId}`)
                this.dirtyCells.add(`${x}-${y + 1}-${layerId}`)
            }
            // A removal (destroy) or a damaged tile drives the tile-effect system -- keep it on the
            // full-rerender path so crumble/shake/holes are not raced by an incremental remove+recreate.
            if (
                delta.removed ||
                (context?.hp !== undefined && context?.hpMax !== undefined && context.hp < context.hpMax)
            ) {
                this.dirtyNeedsFull = true
            }
            if (delta.removed) {
                this.lavaTileFade.cancel(`${x}-${y}-${layerId}`)
                // Taken down: built again, it lands again. A removal names no material; the tile it removes does.
                const removing = this.tilemapMetaProvider.getMapDeltaAt(x, y, layerId)
                const landingLayer = removing && this.landingLayerOf({ ...removing, layerId })
                if (landingLayer) buildLandings.forget(this.storey(), landingLayer, x, y)
                // The removal delta and the wall's "destroy" tile-effect race; the removal (which drives
                // the surface-clearing rerender below) can win, so the destroy effect then finds no
                // surface to shatter. Snapshot this cell's shatter textures NOW -- the surface + its
                // live sprites still exist at this point -- so a destroy effect arriving after the
                // rerender can still play the shatter from the snapshot.
                const removedSurfaceId = this.spritedCellSurfaceId.get(`${x}-${y}-${layerId}`)
                if (removedSurfaceId) {
                    this.tileEffectSystem.snapshotSurfaceForDestroy(removedSurfaceId)
                }
                this.tilemapMetaProvider.removeTileAt(x, y, layerId)
            } else {
                const layerMapping = this.tilemapMetaProvider.resolveLayerMapping(layerId)
                const tileMap =
                    this.tilemapMetaProvider.resolveTileMapFromLayerId(layerId) ||
                    this.tilemapMetaProvider.entityTileMap()
                const isReplace = layerMapping?.maptype === "tilemap" && tileMap[x][y] !== "0"
                if (layerMapping?.maptype === "tilemap") {
                    const key = `${x}-${y}-${layerId}`
                    const isLava = this.tilemapMetaProvider.resolveTileMeta(tileId).tileTypes.includes("lava")
                    const wasLava = this.tilemapMetaProvider.resolveTileMeta(tileMap[x][y]).tileTypes.includes("lava")
                    if (isLava && !wasLava) this.lavaTileFade.start(key)
                    else if (!isLava) this.lavaTileFade.cancel(key)
                }

                // Incoming server state is authoritative, including increasing HP during repair.
                // Art-only updates may omit health; keep the existing values in that case.
                const existingDelta = this.tilemapMetaProvider.getMapDeltaAt(x, y, layerId)
                const mergedContext = { ...existingDelta?.context,
                    ...Object.fromEntries(Object.entries(context || {}).filter(([, value]) => value !== undefined)),
                }

                this.tilemapMetaProvider.addTileAt(
                    x,
                    y,
                    blocking,
                    occlusion,
                    tileId,
                    layerId,
                    mapSpriteCap,
                    temporary,
                    collideable,
                    mergedContext,
                )

                // Ask for a repaint. A delta that ADDS a tile is the only notice the client gets for
                // a tile that appears with no placement behind it: the sapling a planted seed becomes,
                // the next stage a sapling grows into, a regrown tree. Those all come from
                // ENTITY_TILE_ADDED, which broadcasts the delta and nothing else -- no redrawClient --
                // so without this the cell lands in the data model only (collision/blocking correct,
                // nothing drawn) and is first PAINTED by the next unrelated repaint or a reload.
                //
                // repaintDirtyCells decides how: per-cell on a sprited layer, a full rerender on a
                // tilemap layer (CompositeTilemap is append+clear only, so a cell whose tile was
                // replaced cannot be repainted in isolation) -- which is the entity-tile surface, and
                // where trees/saplings are composite tiles baked by the full render path anyway.
                //
                // Removals and damage are deliberately NOT self-painting: the tile-effect system
                // (crumble, shatter, damage holes) owns those cells and an incremental
                // remove+recreate races it. See dirtyNeedsFull.
                this.pendingRedraw = true

                // if (mapSpriteCap) {
                //     this.tilemapMetaProvider.addMapSpriteCapAt(x, y, mapSpriteCap?.context)
                // }
            }
        } else {
            this.tilemapMetaProvider.updateBlockingAt(x, y, blocking, false)
        }
    }

    // ============================================================================
    // PIECES LANDING AS THEY ARE BUILT (effects/build_landing.ts)
    // ============================================================================

    /** Which storey of its building this map draws (0 on the ground, or with no building). */
    private storey = (): number => this.tilemapMetaProvider?.twoStoreyHouse?.floor ?? 0

    /** The kind of built piece a delta carries: a wall on the sprite layer, a roof in the sky, a floor. */
    private landingLayerOf = (delta: MapDelta): LandingLayer | undefined => {
        const buildType = ItemTypeCharacteristics[delta.context?.itemType]?.buildType
        if (!buildType || !this.tilemapMetaProvider) return undefined
        const maptype = this.tilemapMetaProvider.resolveLayerMapping(delta.layerId)?.maptype
        if (buildType === "wall") return delta.layerId === "sprite" ? "wall" : undefined
        if (buildType === "roof") return maptype === "sky" ? "roof" : undefined
        return (buildType === "floor" || buildType === "floor2") && maptype === "tilemap" ? "floor" : undefined
    }

    /**
     * The pieces a batch of deltas builds anew, by kind. A wall counts once, at its own (parent) cell,
     * and only if that cell wasn't already this wall: a neighbour that merely changes shape, or a
     * window fitted to a wall, does not land. Roofs and floors count cell by cell.
     */
    private newBuildPieces = (deltas: MapDelta[]) => {
        const provider = this.tilemapMetaProvider
        const built = new Map<LandingLayer, { cells: Coordinate[]; tiles: { tileId: string; layerId: string }[]; itemType: string }>()
        if (!provider) return built
        deltas.forEach(delta => {
            if (!delta?.tileId || delta.removed) return
            const layer = this.landingLayerOf(delta)
            if (!layer) return
            const { x, y } = delta
            if (layer === "wall") {
                const parent = delta.context?.parent
                if (parent?.x !== x || parent?.y !== y) return
                const before = provider.getMapDeltaAt(x, y, "sprite")
                if (before && !before.removed && before.context?.parent?.x === x && before.context?.parent?.y === y) return
            } else {
                const before = provider.resolveTileMapFromLayerId(delta.layerId)?.[x]?.[y]
                if (before && before !== "0") return
            }
            let group = built.get(layer)
            if (!group) built.set(layer, group = { cells: [], tiles: [], itemType: delta.context.itemType })
            group.cells.push({ x, y })
            group.tiles.push({ tileId: delta.tileId, layerId: delta.layerId })
        })
        return built
    }

    /** Land what a batch built. The builder's own client already started these at its print, and keeps that timeline. */
    private landBuiltPieces = (built: ReturnType<MapRenderingManager["newBuildPieces"]>) => {
        const now = Date.now()
        built.forEach(({ cells, tiles, itemType }, layer) => {
            const started = buildLandings.start(cells, { storey: this.storey(), layer, now, origin: this.builderNear(cells), itemType,
                lift: layer === "roof" ? houseRoofOffset() : 0, tiles })
            if (layer !== "wall") started.forEach(this.addLandingOverlay)
        })
    }

    /** Where the wave of a peer's group starts: the player nearest to it, who most likely built it. */
    private builderNear = (cells: Coordinate[]): Coordinate | undefined => {
        const cx = cells.reduce((sum, c) => sum + c.x + 0.5, 0) / cells.length
        const cy = cells.reduce((sum, c) => sum + c.y + 0.5, 0) / cells.length
        let nearest: Coordinate | undefined, distance = Infinity
        this.logic?.entityManager?.iterate("player", (entity: Entity) => {
            if (!entity.location) return
            const at = { x: entity.location.x / TileSize, y: entity.location.y / TileSize }
            const d = Math.hypot(at.x - cx, at.y - cy)
            if (d < distance) { nearest = at; distance = d }
        })
        return distance < 16 ? nearest : undefined
    }

    /**
     * A peer's roof or floor piece lands as a copy drawn over the real one, which sits in a tilemap and
     * can't be moved on its own. The copy only ever grows from its rest size, so it always covers it.
     */
    private addLandingOverlay = (landing: Landing): void => {
        if (!landing.tileId || !landing.layerId) return
        if (landing.layer === "roof" && this.tilemapMetaProvider.isRoofCoveredAt(landing.x, landing.y)) return
        const sprite = this.addTileAt(landing.x, landing.y, landing.tileId, landing.layerId, true)?.sprite
        if (!sprite) return
        // A roof is drawn over everything; a floor under every body.
        sprite.zIndex = landing.layer === "roof" ? 100000 : 0.5
        this.landingOverlays.set(landing, { sprites: [sprite], pivot: { x: sprite.x + TileSize / 2, y: sprite.y + TileSize / 2 } })
    }

    /** How much of this storey's roof shows: none from inside the house it covers. */
    private roofLandingAlpha = (): number => {
        const house = this.tilemapMetaProvider?.getHousePresentation()
        if (hasHouseRoof(house)) return this.houseRoof.visible ? this.houseRoof.alpha : 0
        return this.skySurface?.visible ? this.skyManager.getSkyAlpha() : 0
    }

    /** The drawn sprites of a landing wall: its own cell and the top piece above it that belongs to it. */
    private landingWallSprites = (landing: Landing): Sprite[] => {
        const sprites: Sprite[] = []
        for (const y of [landing.y, landing.y - 1]) {
            const parent = this.tilemapMetaProvider?.getMapDeltaAt(landing.x, y, "sprite")?.context?.parent
            if (parent?.x !== landing.x || parent?.y !== landing.y) continue
            this.spritedCellSprites.get(`${landing.x}-${y}-sprite`)?.forEach(sprite => { if (!sprite.destroyed) sprites.push(sprite) })
        }
        return sprites
    }

    /** Every frame: pose what is landing, put down what has landed, and kick up its dust at impact. */
    private updateLandings = (): void => {
        const now = Date.now()
        buildLandings.prune(now)
        const storey = this.storey()
        const duration = landingDurationMs()
        buildLandings.forEach(landing => {
            const elapsed = now - landing.startAt
            const done = elapsed >= duration
            if (landing.ghosts?.size) this.poseLandingGhosts(landing, now, done, storey)
            if (landing.storey !== storey || this.settledLandings.has(landing)) return
            if (landing.layer === "wall") {
                const sprites = this.landingWallSprites(landing)
                if (done) restSprites(sprites)
                else poseSprites(sprites, landingPose(elapsed, "wall", 0.7),
                    { x: (landing.x + 0.5) * TileSize, y: (landing.y + 1) * TileSize })
            }
            const overlay = this.landingOverlays.get(landing)
            if (overlay) {
                if (done) {
                    overlay.sprites.forEach(sprite => { sprite.parent?.removeChild(sprite); if (!sprite.destroyed) sprite.destroy() })
                    this.landingOverlays.delete(landing)
                } else {
                    poseSprites(overlay.sprites, landingPose(elapsed, landing.layer), overlay.pivot, false)
                    const alpha = landing.layer === "roof" ? this.roofLandingAlpha() : 1
                    overlay.sprites.forEach(sprite => { sprite.alpha = alpha })
                }
            }
            if (landing.dust && !landing.dusted && elapsed >= landingImpactMs()) {
                landing.dusted = true
                // A tab that stalled through the impact doesn't owe the dust any more.
                if (elapsed < duration + 100) this.puffLandingDust(landing, now)
            }
            if (done) this.settledLandings.add(landing)
        })
        this.landingDust.update(now)
    }

    /**
     * The builder's own ghosts ride the landing until the real piece takes over: a wall as soon as this
     * map draws it (posed from the same clock, so nothing jumps), a roof or floor once it has landed.
     * The short waits cover the frame between the server's reply and its tiles being drawn.
     */
    private poseLandingGhosts = (landing: Landing, now: number, done: boolean, storey: number): void => {
        const ghosts = [...landing.ghosts].filter(sprite => !sprite.destroyed && sprite.parent === this.surface)
        if (!ghosts.length) return
        if (landing.ghostsOwned) {
            const since = now - (landing.releasedAt ?? 0)
            const replaced = landing.layer === "wall"
                ? (landing.storey === storey && this.landingWallSprites(landing).length > 0) || (done && since > 300)
                : done && since > 100
            if (replaced) {
                endLandingGhosts(landing)
                return
            }
        }
        if (done) restSprites(ghosts, false)
        else poseSprites(ghosts, landingPose(now - landing.startAt, landing.layer), landing.ghostPivot, false)
    }

    private puffLandingDust = (landing: Landing, now: number): void => {
        if (landing.layer === "roof" && this.roofLandingAlpha() === 0) return
        const colors = landingDustColors(landing.itemType)
        const seed = (landing.x * 73856093) ^ (landing.y * 19349663) ^ (landing.storey * 83492791)
        const strength = landing.dust === 2 ? 2 : 1
        const middle = (landing.x + 0.5) * TileSize
        if (landing.layer === "wall") {
            // Off the wall's foot, just in front of it.
            this.landingDust.puff(middle, (landing.y + 1) * TileSize - 3, TileSize / 2 - 3, colors, strength,
                spritedTileZIndex(this.spritedLayerIndex("sprite"), landing.y) + 0.4, now, seed)
        } else {
            this.landingDust.puff(middle, (landing.y + 1) * TileSize - 2 + landing.lift, TileSize / 2 - 2, colors, strength,
                landing.layer === "roof" ? 100000.1 : 0.6, now, seed)
        }
    }

    private clearLandingArt = (): void => {
        this.landingOverlays.forEach(({ sprites }) => sprites.forEach(sprite => {
            sprite.parent?.removeChild(sprite)
            if (!sprite.destroyed) sprite.destroy()
        }))
        this.landingOverlays.clear()
        this.landingDust.clear()
    }

    // ============================================================================
    // GETTERS
    // ============================================================================

    getVisibilityMap = () => (!this.visibility?.mask ? undefined : this.visibility.mask)
    crossFloorAim = (point: import("game-common/models").Coordinate) =>
        this.sceneRenderer.projectileAim(point, this.tilemapMetaProvider?.twoStoreyHouse) ??
        this.sceneRenderer.surfaceAim(point, this.tilemapMetaProvider?.twoStoreyHouse)
    isRoofVisibleForPlacement = (): boolean => this.skySurface.visible && this.skyManager.getSkyAlpha() > .15

    drawnStoreys = () => this.sceneRenderer.drawnStoreys(this.tilemapMetaProvider?.twoStoreyHouse)
    sceneTileEffect = (effect: TileEffect) => this.sceneRenderer.tileEffect(effect)

    getSceneEntity = (id: string) => this.sceneRenderer.getEntity(id)
    getSceneRenderable = (id: string) => this.sceneRenderer.getRenderable(id)
    getSceneLights = () => {
        const scene = this.sceneRenderer.getLights()
        const rock = this.rockMass.lights()
        return rock.length ? [...scene, ...rock] : scene
    }
    updateSceneRooms = (rooms: import("game-common/models").SceneRoomState[]) => {
        this.sceneRenderer.update(rooms, this.tilemapMetaProvider?.getHousePresentation())
        if (this.tilemapMetaProvider) this.sceneRenderer.linkFloorSupport(this.tilemapMetaProvider)
    }

    getVisibility = (): Visibility | undefined => this.visibility

    getCameraCenter = () => this.cameraManagementService.getTranslationComputer().getCameraCenter()

    get translationComputer() {
        return this.cameraManagementService.getTranslationComputer()
    }
}
